import { createRouter as createVueRouter, createWebHistory } from 'vue-router'
import moment from 'moment'

import 'moment/dist/locale/tr'
import 'moment/dist/locale/ro'
import Nprogress from 'nprogress'
import { event, set } from 'vue-gtag'
import { useAdminAuthStore } from '@/stores/admin/auth.js'
import { useFarmerDocumentStore } from '@/stores/admin/farmerDocument.js'
import { useOrderDocumentStore } from '@/stores/admin/orderDocument.js'
import { useNotificationStore } from '@/stores/helpers/notification.js'
import { usePageLoadingStore } from '@/stores/helpers/pageLoading.js'
import { useAuthStore } from '@/stores/retailer/auth.js'
import ConsumerGoodsCustomerInfoService from '@/services/retailer/ConsumerGoodsCustomerInfoService.js'
import RetailerCarSalesService from '@/services/retailer/RetailerCarSalesService.js'
import RetailerIyiFinansCarSalesService from '@/services/retailer/RetailerIyiFinansCarSalesService.js'
import RetailerTractorSalesService from '@/services/retailer/RetailerTractorSalesService.js'
import i18n from '@/i18n/i18n.js'
import adminNotRequireAuth from '@/middlewares/adminNotRequireAuth.js'
import adminRequireAuth from '@/middlewares/adminRequireAuth.js'
import checkCountryAccess from '@/middlewares/checkCountryAccess.js'
import deprecatedRoute from '@/middlewares/deprecatedRoute.js'
import middlewarePipeline from '@/middlewares/middlewarePipeline.js'
import retailerCarSalesBlocker from '@/middlewares/retailerCarSalesBlocker.js'
import retailerClosedSalesChannelBlocker from '@/middlewares/retailerClosedSalesChannelBlocker.js'
import retailerCompanyActivityControl from '@/middlewares/retailerCompanyActivityControl.js'
import retailerConsumerGoodsBlocker from '@/middlewares/retailerConsumerGoodsBlocker.js'
import retailerForwardSaleBlocker from '@/middlewares/retailerForwardSaleBlocker.js'
import retailerIyiFinansBlocker from '@/middlewares/retailerIyiFinansBlocker.js'
import retailerMachineOwnership from '@/middlewares/retailerMachineOwnership.js'
import retailerNotRequireAuth from '@/middlewares/retailerNotRequireAuth.js'
import retailerPolicyControl from '@/middlewares/retailerPolicyControl.js'
import retailerRequireAuth from '@/middlewares/retailerRequireAuth.js'
import retailerSalesChannelControl from '@/middlewares/retailerSalesChannelControl.js'
import ro from '@/routes/ro.js'
import country from '@/utilities/country.js'
import Retailer from '@/utilities/retailerHelper.js'
import { FARMER_DOCUMENT_CATEGORY_MAP } from '@/constants/farmerDocumentCategory.js'
import { ORDER_DOCUMENT_TYPE, ORDER_DOCUMENT_TYPE_MAP } from '@/constants/orderDocumentType.js'
import { RO_ROLE_ROUTES, TR_ROLE_ROUTES } from '@/constants/roleRoutes.js'
import Admin from '@/views/layouts/Admin.vue'
import Default from '@/views/layouts/Default.vue'
import Panel from '@/views/layouts/Panel.vue'
import Romania from '@/views/layouts/Romania.vue'
import FarmerInvoice from '@/views/pages/FarmerInvoice.vue'
import Maintenance from '@/views/pages/Maintenance.vue'
import NetworkIssue from '@/views/pages/NetworkIssue.vue'
import NotFound from '@/views/pages/NotFound.vue'

const { t } = i18n.global

const createRoutes = () => {
  return [
    {
      path: '/',
      component: Default,
      meta: {
        title: t('views.retailer.users.login.title')
      },
      children: [
        {
          path: '',
          name: 'home',
          beforeEnter: retailerNotRequireAuth,
          component: () => import('@/views/retailer/users/login.vue')
        }
      ]
    },
    {
      path: '/',
      component: Default,
      children: [
        {
          path: 'admin/login',
          name: 'admin.users.login',
          beforeEnter: adminNotRequireAuth,
          component: () => import('@/views/admin/users/login.vue')
        },
        {
          path: 'admin/verify',
          name: 'admin.users.verify',
          beforeEnter: adminRequireAuth,
          component: () => import('@/views/admin/users/verify.vue')
        },
        {
          path: 'login',
          name: 'panel-login',
          redirect: { name: 'home' },
          beforeEnter: retailerNotRequireAuth,
          component: () => import('@/views/retailer/users/login.vue')
        },
        {
          path: 'register',
          name: 'panel-register',
          beforeEnter: country.is('ro') ? retailerNotRequireAuth : retailerCompanyActivityControl,
          meta: {
            title: t('views.retailer.users.register.title')
          },
          component: () =>
            country.is('ro')
              ? import('@/views/retailer/users/romania/register.vue')
              : import('@/views/retailer/users/register.vue')
        },
        {
          path: 'forgot-password',
          name: 'forgot-password',
          beforeEnter: retailerNotRequireAuth,
          meta: {
            title: t('views.retailer.users.forgot_password.title')
          },
          component: () => import('@/views/retailer/users/forgotPassword.vue')
        },
        {
          path: 'reset-password/:token',
          name: 'reset-password',
          beforeEnter: retailerNotRequireAuth,
          component: () => import('@/views/retailer/users/resetPassword.vue')
        },
        {
          path: '/404',
          name: '404',
          component: NotFound,
          props: true
        },
        {
          path: '/network-issue',
          name: 'network-issue',
          component: NetworkIssue,
          props: true
        },
        {
          path: '/maintenance',
          name: 'maintenance',
          component: Maintenance
        },
        {
          path: '/farmer-invoice/:id',
          name: 'farmer-invoice',
          component: FarmerInvoice,
          props: true
        },
        {
          path: '/invoices/:hash',
          name: 'invoices',
          component: () => import('@/views/pages/OrderInvoice.vue')
        },
        {
          path: '/farmer-required-documents/:id',
          name: 'farmer-required-documents',
          component: () => import('@/views/pages/FarmerRequiredDocuments.vue'),
          props: true
        },
        {
          path: '/farmer-documents/:farmerHash/:retailerUserHash',
          name: 'farmer-documents',
          component: () => import('@/views/pages/FarmerUploadDocument.vue'),
          props: true,
          meta: {
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: '/order-documents/:orderHash/:retailerUserHash',
          name: 'order-documents',
          component: () => import('@/views/pages/OrderDocumentUpload.vue'),
          props: true,
          meta: {
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: '/order-documents/vehicle-registration/:orderHash/:retailerUserHash',
          name: 'order-documents-vehicle-registration',
          component: () => import('@/views/pages/OrderDocumentUpload.vue'),
          props: true,
          meta: {
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: '/farmer-kyc-documents/:id',
          name: 'farmer-kyc-documents',
          component: () => import('@/views/pages/FarmerKycDocument.vue'),
          props: true
        },
        {
          path: '/farmer-paid-promissory-note/:order_id',
          name: 'farmer-paid-promissory-note',
          component: () => import('@/views/pages/FarmerPaidPromissoryNote.vue'),
          props: true
        },
        {
          path: 'gdpr/:tarfinCompanyTypeForRo/:gdprConsentLinkHash',
          name: 'gdpr-consent',
          component: () => import('@/views/retailer/applications/international/romania/gdprConsent.vue')
        },
        {
          path: 'kvkk/:gdprConsentLinkHash',
          name: 'gdpr-consent',
          component: () => import('@/views/pages/GdprConsent.vue')
        },
        {
          path: 'car-sales/:machineId',
          name: 'car-sales-application',
          component: () => import('@/views/pages/car-sales/CarSalesApplication.vue'),
          props: true
        },
        {
          path: 'documents/:machineId',
          name: 'public-documents',
          component: () => import('@/views/pages/documents/DocumentUpload.vue'),
          props: true
        },
        {
          path: 'residence-document/:machineId',
          name: 'customer-residence-document',
          component: () => import('@/views/pages/consumer-goods/ConsumerGoodsDocumentUpload.vue'),
          props: true
        },
        {
          path: 'iyi-finans-car-sales/:machineId',
          name: 'iyi-finans-car-sales-application',
          component: () => import('@/views/pages/iyi-finans-car-sales/IyiFinansCarSalesApplication.vue'),
          props: true
        },
        {
          path: 'tractor-sales/:machineId',
          name: 'tractor-sales-application',
          component: () => import('@/views/pages/tractor-sales/TractorSalesApplication.vue'),
          props: true
        },
        {
          path: 'customer-detail/:machineId',
          name: 'customer-detail',
          component: () => import('@/views/pages/customer-detail/CustomerDetail.vue'),
          props: true
        },
        {
          path: 'retailer/applications/car-sales/:machineId/customer-info',
          name: 'retailer.applications.car-sales.customer-info',
          beforeEnter: [
            retailerRequireAuth,
            retailerSalesChannelControl,
            retailerPolicyControl,
            retailerConsumerGoodsBlocker
          ],
          component: () => import('@/views/pages/car-sales/CarSalesApplication.vue'),
          props: true,
          meta: {
            machineOwnership: RetailerCarSalesService,
            breadcrumb: { name: 'Araç Satış Başvurusu' },
            scrollPosition: 0,
            allowForCarSales: true,
            allowForAgriculture: false,
            mode: 'retailer',
            middleware: [retailerCarSalesBlocker]
          }
        },
        {
          path: 'retailer/applications/iyi-finans-car-sales/:machineId/customer-info',
          name: 'retailer.applications.iyi-finans-car-sales.customer-info',
          beforeEnter: [
            retailerRequireAuth,
            retailerSalesChannelControl,
            retailerPolicyControl,
            retailerConsumerGoodsBlocker
          ],
          component: () => import('@/views/pages/iyi-finans-car-sales/IyiFinansCarSalesApplication.vue'),
          props: true,
          meta: {
            machineOwnership: RetailerIyiFinansCarSalesService,
            breadcrumb: { name: 'Araç Satış Başvurusu' },
            scrollPosition: 0,
            allowForIyiFinans: true,
            allowForAgriculture: false,
            mode: 'retailer',
            middleware: [retailerIyiFinansBlocker]
          }
        },
        {
          path: 'retailer/applications/tractor-sales/:machineId/customer-info',
          name: 'retailer.applications.tractor-sales.customer-info',
          beforeEnter: [
            retailerRequireAuth,
            retailerSalesChannelControl,
            retailerPolicyControl,
            retailerConsumerGoodsBlocker
          ],
          component: () => import('@/views/pages/tractor-sales/TractorSalesApplication.vue'),
          props: true,
          meta: {
            machineOwnership: RetailerTractorSalesService,
            breadcrumb: { name: 'Traktör Satış Başvurusu' },
            scrollPosition: 0,
            allowForTractorSales: true,
            allowForAgriculture: false,
            mode: 'retailer'
          }
        },
        {
          path: 'upload-gdpr-document-mobile/:tckn',
          name: 'upload-gdpr-document-mobile',
          component: () => import('@/views/retailer/applications/international/romania/uploadGdprDocument.vue')
        },
        {
          path: 'tarfin-pro-document-mobile-upload/:hash',
          name: 'tarfin-pro-document-mobile-upload',
          component: () => import('@/views/retailer/tarfinProRequests/uploadMobileDocument.vue'),
          meta: {
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: '/farmer-satisfaction-surveys/:hash',
          name: 'farmer-satisfaction-surveys',
          component: () => import('@/views/pages/FarmerSatisfactionSurvey.vue'),
          props: true,
          meta: {
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'upload-required-documents/:hash',
          name: 'upload-required-documents-mobile',
          component: () => import('@/views/retailer/applications/components/uploadMobileDocument.vue'),
          meta: {
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'digital-signable-document/:applicationHash/:documentHash',
          name: 'digital-signable-document',
          component: () => import('@/views/retailer/applications/international/romania/digitalSignableDocument.vue'),
          meta: {
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: '/overdue-payment-information/:hash',
          name: 'overdue-payment-information',
          component: () => import('@/views/pages/OverduePaymentInformation.vue'),
          props: true,
          meta: {
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: '/credit-card-payment/:hash',
          name: 'credit-card-payment-page',
          component: () => import('@/views/pages/CreditCardPayment.vue'),
          props: true,
          meta: {
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: '/credit-card-payment-success/:hash',
          name: 'credit-card-payment-success-page',
          component: () => import('@/views/pages/successedCreditCardPayment.vue'),
          props: true,
          meta: {
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: '/failed-pay-with-credit-card',
          name: 'credit-card-payment-failed-page',
          component: () => import('@/views/pages/failedCreditCardPayment.vue'),
          props: true,
          meta: {
            suspense: {
              enabled: true
            }
          }
        }
      ]
    },
    {
      path: '/admin',
      component: Admin,
      beforeEnter: adminRequireAuth,
      meta: {
        breadcrumb: { name: t('router.dashboard') }
      },
      children: [
        {
          path: '',
          name: 'admin.dashboard',
          component: () => import('@/views/admin/dashboard/dashboard.vue'),
          meta: {
            breadcrumb: { name: t('router.dashboard') },
            permissions: ['view-dashboard'],
            suspense: {
              enabled: true
            }
          }
        },

        {
          path: 'pending-approvals',
          name: 'admin.approvals.index',
          component: () => import('@/views/admin/approvals/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.pending_approvals'),
              suspense: {
                enabled: true
              }
            },
            permissions: ['view-any-approvals']
          }
        },

        {
          path: 'archived-approvals',
          name: 'admin.approvals.archive',
          component: () => import('@/views/admin/approvals/archive.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.archived_approvals'),
              suspense: {
                enabled: true
              }
            },
            permissions: ['view-any-approvals']
          }
        },

        // region Sales Reports
        {
          path: 'sales-report',
          name: 'admin.reports.sales.index',
          component: () => import('@/views/admin/reports/sales/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.reports') },
            permissions: ['view-sales-report']
          }
        },
        // endregion

        // region TMKS Refund Reports
        {
          path: 'tmks-refund-report',
          name: 'admin.reports.tmksRefund.index',
          component: () => import('@/views/admin/reports/tmksRefund/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.tmks_refund_report') },
            permissions: ['view-tmks-refund-report'],
            suspense: {
              enabled: true
            }
          }
        },
        // endregion

        // region Incoming Payments
        {
          path: 'incoming-payments',
          name: 'admin.incoming-payments.index',
          component: () => import('@/views/admin/incomingPayments/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.incoming_payments') },
            permissions: ['view-any-bank-payment'],
            suspense: {
              enabled: true
            }
          }
        },

        // endregion

        // region Underwriting Decision Reports
        {
          path: 'underwriting-decision-report',
          name: 'admin.reports.underwritingDecision.index',
          component: () => import('@/views/admin/reports/underwritingDecision/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.underwriting_decision_report') },
            permissions: ['view-underwriting-decision-report']
          }
        },
        // endregion

        // region Payment Performance Reports
        {
          path: 'payment-performance-report',
          name: 'admin.reports.paymentPerformance.index',
          component: () => import('@/views/admin/reports/paymentPerformance/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.reports') },
            permissions: ['view-payment-performance-report']
          }
        },
        {
          path: 'finance-portfolio-report',
          name: 'admin.reports.financePortfolio.index',
          component: () => import('@/views/admin/reports/financePortfolio/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.reports') },
            permissions: ['view-finance-portfolio-report']
          }
        },
        {
          path: 'mobile-price-report',
          name: 'admin.reports.mobilePrice.index',
          component: () => import('@/views/admin/reports/mobilePrice/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.mobile_price_report') },
            permissions: ['view-mobile-price-report']
          }
        },
        // endregion

        // region Applications
        {
          path: 'applications',
          name: 'admin.applications.index',
          props: true,
          component: () => import('@/views/admin/applications/index.vue'),
          meta: {
            breadcrumb: { name: t('router.applications') },
            permissions: ['view-any-application', 'view-any-retailer', 'view-any-sales-channel']
          }
        },
        {
          path: 'applications/:id',
          name: 'admin.applications.show',
          props: true,
          component: () => import('@/views/admin/applications/show.vue'),
          meta: {
            breadcrumb: {
              name: t('router.application_detail'),
              parent: 'admin.applications.index',
              suspense: { enabled: true }
            },
            permissions: ['view-any-application', 'view-application']
          }
        },
        {
          path: 'applications/:id/keysfin-report',
          name: 'admin.applications.keysfin.show',
          props: true,
          component: () => import('@/views/admin/applications/keysfinReport/show.vue'),
          meta: {
            breadcrumb: {
              name: t('router.keysfin_report'),
              parent: 'admin.applications.show'
            },
            permissions: ['view-any-application', 'view-application'],
            suspense: {
              enabled: true
            }
          }
        },
        // endregion

        // region Users
        {
          path: 'users',
          name: 'admin.users.index',
          component: () => import('@/views/admin/users/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.users') },
            permissions: ['view-any-user']
          }
        },
        {
          path: 'users/create',
          name: 'admin.users.create',
          component: () => import('@/views/admin/users/create.vue'),
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.users.index',
              suspense: { enabled: true }
            },
            permissions: ['create-user']
          }
        },
        {
          path: 'users/:id',
          name: 'admin.users.show',
          component: () => import('@/views/admin/users/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.detail'),
              parent: 'admin.users.index'
            },
            permissions: ['view-user', 'view-any-role']
          }
        },
        {
          path: 'users/:id/edit',
          name: 'admin.users.edit',
          component: () => import('@/views/admin/users/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.users.index',
              suspense: { enabled: true }
            },
            permissions: ['view-user', 'update-user']
          }
        },
        {
          path: 'account/general',
          name: 'admin.account.general',
          component: () => import('@/views/admin/account/edit.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.account') }
          }
        },
        // endregion

        // region Payments
        {
          path: 'retailers/:id/payments/payable',
          name: 'admin.retailers.payments.index',
          component: () => import('@/views/admin/retailerPayments/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.make_payment'),
              parent: 'admin.retailers.show'
            },
            permissions: ['create-retailer-payment']
          }
        },
        {
          path: 'retailers/:id/netting',
          name: 'admin.retailers.netting',
          component: () => import('@/views/admin/retailers/netting.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.netting'),
              parent: 'admin.retailers.show'
            },
            permissions: ['netting']
          }
        },
        // endregion

        // region Holidays
        {
          path: 'holidays',
          name: 'admin.holidays.index',
          component: () => import('@/views/admin/holidays/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.holidays') },
            permissions: ['view-any-holiday']
          }
        },
        {
          path: 'holidays/create',
          name: 'admin.holidays.create',
          component: () => import('@/views/admin/holidays/create.vue'),
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.holidays.index'
            },
            permissions: ['create-holiday']
          }
        },
        {
          path: 'holidays/:id/edit',
          name: 'admin.holidays.edit',
          component: () => import('@/views/admin/holidays/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.holidays.index'
            },
            permissions: ['view-holiday', 'update-holiday']
          }
        },
        // endregion

        // region Categories
        {
          path: 'categories',
          name: 'admin.categories.index',
          component: () => import('@/views/admin/categories/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.categories') },
            permissions: ['view-any-category']
          }
        },
        {
          path: 'categories/create',
          name: 'admin.categories.create',
          component: () => import('@/views/admin/categories/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.categories.index'
            },
            permissions: ['create-category']
          }
        },
        {
          path: 'categories/:id/edit',
          name: 'admin.categories.edit',
          component: () => import('@/views/admin/categories/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.categories.index'
            },
            permissions: ['view-category', 'update-category']
          }
        },
        {
          path: 'categories/:category_id/sub-categories',
          name: 'admin.categories.sub-categories.index',
          component: () => import('@/views/admin/subCategories/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.sub_categories'),
              parent: 'admin.categories.index'
            },
            permissions: ['view-any-category']
          }
        },
        {
          path: 'categories/:category_id/sub-categories/create',
          name: 'admin.categories.sub-categories.create',
          component: () => import('@/views/admin/subCategories/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.categories.sub-categories.index'
            },
            permissions: ['view-category', 'create-category']
          }
        },
        {
          path: 'categories/:category_id/sub-categories/:id/edit',
          name: 'admin.categories.sub-categories.edit',
          component: () => import('@/views/admin/subCategories/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.categories.sub-categories.index'
            },
            permissions: ['view-category', 'update-category']
          }
        },
        {
          path: 'categories/:category_id/sub-categories/:id/products',
          name: 'admin.sub-categories.products.index',
          component: () => import('@/views/admin/subCategoryProducts/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.products'),
              parent: 'admin.categories.sub-categories.index'
            },
            permissions: ['view-any-product']
          }
        },
        {
          path: 'categories/:category_id/brands',
          name: 'admin.categories.brands.index',
          component: () => import('@/views/admin/categories/brands/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.category_brands'),
              parent: 'admin.categories.index'
            },
            permissions: ['view-any-brand']
          }
        },
        {
          path: 'categories/:category_id/brands/create',
          name: 'admin.categories.brands.create',
          component: () => import('@/views/admin/categories/brands/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add_category_brand'),
              parent: 'admin.categories.brands.index'
            },
            permissions: ['create-brand']
          }
        },
        // endregion

        // region Products
        {
          path: 'products',
          name: 'admin.products.index',
          component: () => import('@/views/admin/products/index.vue'),
          meta: {
            breadcrumb: { name: t('router.products') },
            permissions: ['view-any-product']
          }
        },
        {
          path: 'products/create',
          name: 'admin.products.create',
          component: () => import('@/views/admin/products/create.vue'),
          meta: {
            breadcrumb: {
              name: t('router.add_product'),
              parent: 'admin.products.index'
            },
            permissions: ['view-any-category', 'view-any-sales-channel', 'create-product']
          }
        },
        {
          path: 'products/:id',
          name: 'admin.products.show',
          component: () => import('@/views/admin/products/show.vue'),
          meta: {
            breadcrumb: {
              name: t('router.product'),
              parent: 'admin.products.index'
            }
          },
          permissions: ['view-product']
        },
        {
          path: 'products/:id/edit',
          name: 'admin.products.edit',
          component: () => import('@/views/admin/products/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit_product'),
              parent: 'admin.products.index'
            },
            permissions: ['view-any-category', 'view-any-sales-channel', 'create-product']
          }
        },
        // endregion

        // region Retailer Product Prices
        {
          path: 'product-prices',
          name: 'admin.product-prices.index',
          component: () => import('@/views/admin/productPrices/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.mobil_product_prices') },
            permissions: ['view-any-product-price']
          }
        },
        // endregion

        // region Product Market Prices
        {
          path: 'product-market-prices',
          name: 'admin.product-market-prices.index',
          component: () => import('@/views/admin/productMarketPrices/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.product_market_prices') },
            permissions: ['view-any-product-price']
          }
        },
        {
          path: 'product-market-prices/:id/edit',
          name: 'admin.product-market-prices.edit',
          component: () => import('@/views/admin/productMarketPrices/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.product-market-prices.index'
            }
          }
        },
        // endregion

        // region Campaigns
        {
          path: 'campaigns',
          name: 'admin.campaigns.index',
          component: () => import('@/views/admin/campaigns/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.campaigns') },
            permissions: ['view-any-campaign']
          }
        },
        {
          path: 'campaigns/create',
          name: 'admin.campaigns.create',
          component: () => import('@/views/admin/campaigns/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.campaigns.index'
            },
            permissions: ['create-campaign']
          }
        },
        {
          path: 'campaigns/:id/edit',
          name: 'admin.campaigns.edit',
          component: () => import('@/views/admin/campaigns/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.campaigns.index'
            },
            permissions: ['view-campaign', 'update-campaign']
          }
        },
        {
          path: 'campaigns/:campaign_id/details',
          name: 'admin.campaigns.detail.index',
          component: () => import('@/views/admin/campaignDetails/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.campaign_details'),
              parent: 'admin.campaigns.index'
            },
            permissions: ['view-any-campaign']
          }
        },
        {
          path: 'campaigns/:campaign_id/details/create',
          name: 'admin.campaigns.detail.create',
          component: () => import('@/views/admin/campaignDetails/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.campaigns.detail.index'
            },
            permissions: ['view-campaign', 'create-campaign']
          }
        },
        // endregion

        // region Tarfin Pro Quotations
        {
          path: 'tarfin-pro-quotations',
          name: 'admin.tarfin-pro-quotations.index',
          component: () => import('@/views/admin/tarfinProQuotations/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.tarfin_pro_quotations')
            },
            permissions: ['view-any-tarfin-pro-quotation']
          }
        },
        {
          path: 'tarfin-pro-quotations/create',
          name: 'admin.tarfin-pro-quotations.create',
          component: () => import('@/views/admin/tarfinProQuotations/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.create_tarfin_pro_quotation')
            },
            permissions: ['create-tarfin-pro-quotation', 'update-tarfin-pro-quotation']
          }
        },
        {
          path: 'tarfin-pro-quotations/:quotationId/documents/:documentId',
          name: 'admin.tarfin-pro-quotations.document',
          component: () => import('@/views/admin/tarfinProQuotations/document.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.document_detail')
            },
            permissions: ['view-tarfin-pro-quotation-ducument']
          }
        },
        {
          path: 'tarfin-pro-quotations/:id/edit',
          name: 'admin.tarfin-pro-quotations.edit',
          component: () => import('@/views/admin/tarfinProQuotations/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.create_tarfin_pro_quotation')
            },
            permissions: ['view-tarfin-pro-quotation', 'update-tarfin-pro-quotation']
          }
        },
        {
          path: 'tarfin-pro-quotations/:id',
          name: 'admin.tarfin-pro-quotations.show',
          component: () => import('@/views/admin/tarfinProQuotations/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.tarfin_pro_request_supply_detail')
            },
            permissions: ['view-tarfin-pro-quotation']
          }
        },
        // endregion

        // region Banks
        {
          path: 'banks',
          name: 'admin.banks.index',
          component: () => import('@/views/admin/banks/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.banks'),
              parent: 'admin.dashboard'
            },
            permissions: ['view-any-bank']
          }
        },
        {
          path: 'banks/create',
          name: 'admin.banks.create',
          component: () => import('@/views/admin/banks/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add_bank'),
              parent: 'admin.banks.index'
            },
            permissions: ['create-bank']
          }
        },
        {
          path: 'banks/:bank_id/edit',
          name: 'admin.banks.edit',
          component: () => import('@/views/admin/banks/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit_bank'),
              parent: 'admin.banks.index'
            },
            permissions: ['view-bank', 'update-bank']
          }
        },
        {
          path: 'banks/:bank_id/correspondent-banks',
          name: 'admin.banks.correspondent-banks.index',
          component: () => import('@/views/admin/banks/correspondentBanks/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.correspondent_banks'),
              parent: 'admin.banks.index'
            },
            permissions: ['view-bank', 'view-any-correspondent-bank']
          }
        },
        {
          path: 'banks/:bank_id/correspondent-banks/create',
          name: 'admin.banks.correspondent-banks.create',
          component: () => import('@/views/admin/banks/correspondentBanks/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add_correspondent_bank'),
              parent: 'admin.banks.correspondent-banks.index'
            },
            permissions: ['view-bank', 'create-correspondent-bank']
          }
        },
        {
          path: 'banks/:bank_id/correspondent-banks/:correspondent_bank_id/edit',
          name: 'admin.banks.correspondent-banks.edit',
          component: () => import('@/views/admin/banks/correspondentBanks/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit_correspondent_bank'),
              parent: 'admin.banks.correspondent-banks.index'
            },
            permissions: ['view-bank', 'update-correspondent-bank']
          }
        },
        // endregion

        // region Financing Activities
        {
          path: 'financing-cost-summary',
          name: 'admin.financing-cost-summary.index',
          component: () =>
            country.is('ro')
              ? import('@/views/admin/financingCostSummary/international/components/romania/index.vue')
              : import('@/views/admin/financingCostSummary/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.financing_cost_summary')
            },
            permissions: ['view-any-financing-cost-summary']
          }
        },
        {
          path: 'financing-cost-summary/:id',
          name: 'admin.financing-cost-summary.show',
          component: () =>
            country.is('ro')
              ? import('@/views/admin/financingCostSummary/international/components/romania/show.vue')
              : import('@/views/admin/financingCostSummary/show.vue'),
          meta: {
            breadcrumb: {
              name: t('router.financing_cost_summary_details'),
              parent: 'admin.financing-cost-summary.index'
            }
          },
          permissions: ['view-financing-cost-summary']
        },

        // endregion

        // region Bank Accounts
        {
          path: 'banks/:bank_id/accounts',
          name: 'admin.banks.accounts.index',
          component: () => import('@/views/admin/bankAccounts/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.bank_accounts'),
              parent: 'admin.banks.index'
            },
            permissions: ['view-any-bank-account']
          }
        },
        {
          path: 'banks/:bank_id/accounts/create',
          name: 'admin.banks.accounts.create',
          component: () => import('@/views/admin/bankAccounts/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add_bank_account'),
              parent: 'admin.banks.accounts.index'
            },
            permissions: ['create-bank-account']
          }
        },
        {
          path: 'banks/:bank_id/accounts/:account_id/edit',
          name: 'admin.banks.accounts.edit',
          component: () => import('@/views/admin/bankAccounts/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit_bank_account'),
              parent: 'admin.banks.accounts.index'
            },
            permissions: ['view-bank-account', 'update-bank-account']
          }
        },
        // endregion

        // region Purchase Periods
        {
          path: 'purchase-periods',
          name: 'admin.purchase-periods.index',
          component: () => import('@/views/admin/purchasePeriods/index.vue'),
          meta: {
            breadcrumb: { name: t('router.purchase_periods') },
            permissions: ['view-any-purchase-period']
          }
        },
        {
          path: 'purchase-periods/create',
          name: 'admin.purchase-periods.create',
          component: () => import('@/views/admin/purchasePeriods/create.vue'),
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.purchase-periods.index'
            },
            permissions: ['create-purchase-period']
          }
        },
        {
          path: 'purchase-periods/:id/edit',
          name: 'admin.purchase-periods.edit',
          component: () => import('@/views/admin/purchasePeriods/edit.vue'),
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.purchase-periods.index'
            },
            permissions: ['view-purchase-period', 'create-purchase-period']
          }
        },
        {
          path: 'purchase-periods/:id/details',
          name: 'admin.purchase-periods.details.index',
          component: () => import('@/views/admin/purchasePeriodDetails/index.vue'),
          meta: {
            breadcrumb: {
              name: t('router.purchase_period_details'),
              parent: 'admin.purchase-periods.index'
            },
            permissions: ['view-purchase-period-details']
          }
        },
        {
          path: 'purchase-periods/:id/details/create',
          name: 'admin.purchase-periods.details.create',
          component: () => import('@/views/admin/purchasePeriodDetails/create.vue'),
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.purchase-periods.details.index'
            },
            permissions: ['create-purchase-period-detail']
          }
        },
        // endregion

        // region Coupons

        {
          path: 'coupons',
          name: 'admin.coupons.index',
          component: () => import('@/views/admin/coupons/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.coupons') },
            permissions: ['view-any-coupon']
          }
        },
        {
          path: 'coupons/create',
          name: 'admin.coupons.create',
          component: () => import('@/views/admin/coupons/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.coupons.index'
            },
            permissions: ['create-coupon']
          }
        },
        {
          path: 'coupons/:id',
          name: 'admin.coupons.show',
          component: () => import('@/views/admin/coupons/show.vue'),
          meta: {
            breadcrumb: {
              name: t('router.coupon_details'),
              parent: 'admin.coupons.index'
            }
          },
          permissions: ['view-coupon']
        },

        // endregion

        // region Retailers
        {
          path: 'retailers',
          name: 'admin.retailers.index',
          component: () => import('@/views/admin/retailers/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.retailers') },
            permissions: ['view-any-retailer', 'view-any-sales-channel']
          }
        },
        {
          path: 'retailers/:id/edit',
          name: 'admin.retailers.edit',
          component: () => import('@/views/admin/retailers/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.retailer_edit'),
              parent: 'admin.retailers.show'
            },
            permissions: ['view-retailer', 'update-retailer']
          }
        },
        {
          path: 'retailers/:id/users/:user_id/edit',
          name: 'admin.retailers.users.edit',
          component: () => import('@/views/admin/retailerUsers/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.retailers.show'
            },
            permissions: ['view-retailer', 'view-retailer-user', 'update-retailer-user']
          }
        },
        {
          path: 'retailers/:id',
          name: 'admin.retailers.show',
          component: () => import('@/views/admin/retailers/show.vue'),
          meta: {
            breadcrumb: {
              name: t('router.retailer'),
              parent: 'admin.retailers.index'
            },
            permissions: ['view-retailer', 'view-any-sales-channel', 'view-any-retailer-payment']
          },
          props: true
        },
        {
          path: 'pending-retailer-payments',
          name: 'admin.pending-retailer-payments.index',
          component: () => import('@/views/admin/pendingRetailerPayments/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.payments') },
            permissions: ['view-any-retailer']
          }
        },
        {
          path: 'retailers/:id/sub-retailers',
          name: 'admin.retailers.sub-retailers.index',
          component: () => import('@/views/admin/retailers/subRetailers/index.vue'),
          meta: {
            breadcrumb: {
              name: t('router.sub_retailers'),
              parent: 'admin.retailers.show'
            },
            permissions: ['view-any-retailer']
          },
          props: true
        },
        {
          path: 'retailers/:id/sub-retailers/create',
          name: 'admin.retailers.sub-retailers.create',
          component: () => import('@/views/admin/retailers/subRetailers/create.vue'),
          meta: {
            breadcrumb: {
              name: t('router.create_sub_reteailer'),
              parent: 'admin.retailers.sub-retailers.index'
            },
            permissions: ['create-retailer']
          },
          props: true
        },
        {
          path: 'retailers/:id/sub-retailers/:sub_id',
          name: 'admin.retailers.sub-retailers.show',
          component: () => import('@/views/admin/retailers/subRetailers/show.vue'),
          meta: {
            breadcrumb: {
              name: t('router.sub_retailer_detail'),
              parent: 'admin.retailers.sub-retailers.index'
            },
            permissions: ['view-retailer']
          },
          props: true
        },
        {
          path: 'retailers/:id/sub-retailers/:sub_id/edit',
          name: 'admin.retailers.sub-retailers.edit',
          component: () => import('@/views/admin/retailers/subRetailers/edit.vue'),
          meta: {
            breadcrumb: {
              name: t('router.edit_sub_retailer'),
              parent: 'admin.retailers.sub-retailers.show'
            },
            permissions: ['update-retailer']
          },
          props: true
        },
        {
          path: 'retailers/:id/brands',
          name: 'admin.retailers.brands.index',
          component: () => import('@/views/admin/retailers/brands/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.retailer_brands'),
              parent: 'admin.retailers.show'
            },
            permissions: ['view-any-brand']
          }
        },
        {
          path: 'retailers/:id/brands/create',
          name: 'admin.retailers.brands.create',
          component: () => import('@/views/admin/retailers/brands/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add_retailer_brand'),
              parent: 'admin.retailers.brands.index'
            },
            permissions: ['create-brand']
          }
        },
        {
          path: 'retailers/:id/documents',
          name: 'admin.retailers.documents.index',
          component: () => import('@/views/admin/retailers/documents/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.retailer_documents'),
              parent: 'admin.retailers.show'
            },
            permissions: ['view-any-retailer-document']
          }
        },
        {
          path: 'retailers/:id/findeks-reports',
          name: 'admin.retailers.findeks-reports',
          component: () => import('@/views/admin/retailers/findeksReports.vue'),
          props: true,
          meta: {
            hideTitle: true,
            breadcrumb: {
              name: t('router.retailer_findeks_reports'),
              parent: 'admin.retailers.show'
            },
            permissions: ['view-any-retailer-findeks-report']
          }
        },
        {
          path: 'retailers/:id/payments',
          name: 'admin.retailers.payments',
          component: () => import('@/views/admin/retailers/payments.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.retailer_payments'),
              parent: 'admin.retailers.show'
            }
          }
        },
        // endregion

        // region Interest Rates
        {
          path: 'base-interest-rates',
          name: 'admin.base-interest-rates.index',
          component: () => import('@/views/admin/baseInterestRates/index.vue'),
          meta: {
            breadcrumb: { name: t('router.base_interest_rate') },
            permissions: ['view-any-base-interest-rate'],
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'base-interest-rates/create',
          name: 'admin.base-interest-rates.create',
          component: () => import('@/views/admin/baseInterestRates/create.vue'),
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.base-interest-rates.index'
            },
            permissions: ['create-base-interest-rate'],
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'interest-rates/create',
          name: 'admin.interest-rates.create',
          component: () => import('@/views/admin/interestRates/create.vue'),
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.interest-rates.index'
            },
            permissions: ['create-base-interest-rate']
          }
        },
        {
          path: 'interest-rates',
          name: 'admin.interest-rates.index',
          component: () => import('@/views/admin/interestRates/index.vue'),
          meta: {
            breadcrumb: { name: t('router.interest_rate') },
            permissions: ['view-any-interest-rate']
          }
        },
        {
          path: 'late-fee-interests',
          name: 'admin.late-fee-interests.index',
          component: () => import('@/views/admin/lateFeeInterests/index.vue'),
          meta: {
            breadcrumb: { name: t('router.late_fee_interests') },
            permissions: ['view-any-late-fee-interest']
          }
        },
        {
          path: 'late-fee-interests/create',
          name: 'admin.late-fee-interests.create',
          component: () => import('@/views/admin/lateFeeInterests/create.vue'),
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.late-fee-interests.index'
            },
            permissions: ['create-late-fee-interest']
          }
        },
        {
          path: 'base-commission-rates',
          name: 'admin.base-commission-rates.index',
          component: () => import('@/views/admin/baseCommissionRates/index.vue'),
          meta: {
            breadcrumb: { name: t('router.payment_definitions') },
            permissions: ['view-any-base-commission-rate']
          }
        },
        {
          path: 'base-commission-rates/create',
          name: 'admin.base-commission-rates.create',
          component: () => import('@/views/admin/baseCommissionRates/create.vue'),
          meta: {
            breadcrumb: {
              name: t('router.add_definition'),
              parent: 'admin.base-commission-rates.index'
            },
            permissions: ['create-base-commission-rate']
          }
        },
        // endregion

        // region Sales Channels
        {
          path: 'sales-channels',
          name: 'admin.sales-channels.index',
          component: () => import('@/views/admin/salesChannels/index.vue'),
          meta: {
            breadcrumb: { name: t('router.sales_channels') },
            permissions: ['view-any-sales-channel']
          }
        },
        {
          path: 'sales-channels/:id/workflows',
          name: 'admin.sales-channels.workflows.index',
          component: () => import('@/views/admin/salesChannelWorkflows/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.order_workflows'),
              parent: 'admin.sales-channels.index'
            },
            permissions: ['view-any-sales-channel', 'view-any-workflow']
          }
        },
        {
          path: 'sales-channels/:id/workflows/create',
          name: 'admin.sales-channels.workflows.create',
          component: () => import('@/views/admin/salesChannelWorkflows/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add_workflow'),
              parent: 'admin.sales-channels.workflows.index'
            },
            permissions: ['view-sales-channels', 'view-any-workflow']
          }
        },
        {
          path: 'sales-channels/:id/workflows/:workflow_id/edit',
          name: 'admin.sales-channels.workflows.edit',
          component: () => import('@/views/admin/salesChannelWorkflows/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit_workflow'),
              parent: 'admin.sales-channels.workflows.index'
            },
            permissions: ['view-sales-channels', 'view-any-workflow']
          }
        },
        {
          path: 'sales-channels/:id/products',
          name: 'admin.sales-channels.products.index',
          component: () => import('@/views/admin/salesChannelProducts/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.products'),
              parent: 'admin.sales-channels.index'
            },
            permissions: ['view-sales-channel']
          }
        },
        {
          path: 'sales-channels/:id/products/create',
          name: 'admin.sales-channels.products.create',
          component: () => import('@/views/admin/salesChannelProducts/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.sales-channels.products.index'
            },
            permissions: ['view-sales-channel', 'view-any-product']
          }
        },
        // endregion

        // region Roles & Permissions
        {
          path: 'roles',
          name: 'admin.roles.index',
          component: () => import('@/views/admin/roles/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.roles') },
            permissions: ['view-any-role']
          }
        },
        {
          path: 'roles/create',
          name: 'admin.roles.create',
          component: () => import('@/views/admin/roles/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.roles.index'
            },
            permissions: ['create-role']
          }
        },
        {
          path: 'roles/:id/edit',
          name: 'admin.roles.edit',
          component: () => import('@/views/admin/roles/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.roles.index'
            },
            permissions: ['view-role']
          }
        },
        {
          path: 'roles/:id/permissions',
          name: 'admin.roles.permissions.index',
          component: () => import('@/views/admin/rolePermissions/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.permissions'),
              parent: 'admin.roles.index'
            },
            permissions: ['view-role', 'view-any-permission']
          }
        },
        {
          path: 'roles/:id/permissions/create',
          name: 'admin.roles.permissions.create',
          component: () => import('@/views/admin/rolePermissions/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.roles.permissions.index'
            },
            permissions: ['view-role', 'view-any-permission', 'create-permission']
          }
        },
        {
          path: 'permissions',
          name: 'admin.permissions.index',
          component: () => import('@/views/admin/permissions/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.permissions') },
            permissions: ['view-any-permission']
          }
        },
        {
          path: 'permissions/create',
          name: 'admin.permissions.create',
          component: () => import('@/views/admin/permissions/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.permissions.index'
            },
            permissions: ['create-permission']
          }
        },
        {
          path: 'permissions/:id/edit',
          name: 'admin.permissions.edit',
          component: () => import('@/views/admin/permissions/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.permissions.index'
            },
            permissions: ['view-permission', 'update-permission']
          }
        },
        // endregion

        // region Surveys
        {
          path: 'questions',
          name: 'admin.questions.index',
          component: () => import('@/views/admin/questions/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.questions') },
            permissions: ['view-any-question']
          }
        },
        {
          path: 'questions/create',
          name: 'admin.questions.create',
          component: () => import('@/views/admin/questions/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.questions.index'
            },
            permissions: ['create-question']
          }
        },
        {
          path: 'questions/:id/choices',
          name: 'admin.questions.choices.index',
          component: () => import('@/views/admin/choices/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.choices'),
              parent: 'admin.questions.index'
            },
            permissions: ['view-question']
          }
        },
        {
          path: 'questions/:id/choices/create',
          name: 'admin.questions.choices.create',
          component: () => import('@/views/admin/choices/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.questions.choices.index'
            },
            permissions: ['view-question']
          }
        },
        {
          path: 'questions/:question_id/choices/:id/edit',
          name: 'admin.questions.choices.edit',
          component: () => import('@/views/admin/choices/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.questions.choices.index'
            }
          }
        },
        {
          path: 'questions/:id/requirement',
          name: 'admin.questions.requirements.create',
          component: () => import('@/views/admin/requirements/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add_choice_requirement'),
              parent: 'admin.questions.choices.index'
            },
            permissions: ['view-any-category', 'view-any-question', 'view-question'],
            suspense: {
              enabled: true
            }
          }
        },
        // endregion

        // region Orders
        {
          path: 'orders',
          name: 'admin.orders.index',
          component: () => import('@/views/admin/orders/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.agricultural_orders') },
            permissions: ['view-any-order', 'view-any-sales-channel'],
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'flex-orders',
          name: 'admin.flex-orders.index',
          component: () => import('@/views/admin/flexOrders/index.vue'),
          props: true,
          beforeEnter: country.is('tr') ? undefined : () => ({ name: 'admin.dashboard' }),
          meta: {
            breadcrumb: { name: t('views.layouts.flex_sales') },
            permissions: ['view-any-flex-order'],
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'orders/:id',
          name: 'admin.orders.show',
          component: () => import('@/views/admin/orders/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.order_detail'),
              // Flex-originated details (?from=flex) point back to Flex Satışları; all others to Satışlar.
              parent: route => (route?.query?.from === 'flex' ? 'admin.flex-orders.index' : 'admin.orders.index')
            },
            permissions: ['view-order', 'view-any-payment', 'view-any-collection', 'view-any-bank']
          }
        },
        {
          path: 'orders/:id/invoice',
          name: 'admin.orders.invoice',
          component: () => import('@/views/admin/orders/invoice.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.invoice'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/supplementary-invoice',
          name: 'admin.orders.supplementary-invoice',
          component: () => import('@/views/admin/orders/supplementaryInvoice.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.supplementary_invoice'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/commission-invoice',
          name: 'admin.orders.commission-invoice',
          component: () => import('@/views/admin/orders/commissionInvoice.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.commission_invoice'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/reversal-invoice',
          name: 'admin.orders.reversal-invoice',
          component: () => import('@/views/admin/orders/reversalInvoice.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.reversal_invoice'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/late-fee-invoice',
          name: 'admin.orders.late-fee-invoice',
          component: () => import('@/views/admin/orders/lateFeeInvoice.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.late_fee_invoice'),
              parent: 'admin.orders.show',
              suspense: { enabled: true }
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/promissory-note',
          name: 'admin.orders.promissory-note',
          component: () => import('@/views/admin/orders/promissoryNote.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.promissory_note'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/payment-notice',
          name: 'admin.orders.payment-notice',
          component: () => import('@/views/admin/orders/paymentNotice.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.payment_notice'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/scanned-promissory-note',
          name: 'admin.orders.scanned-promissory-note',
          component: () => import('@/views/admin/orders/scannedPromissoryNote.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.scanned_promissory_note'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/installment-sales-contract',
          name: 'admin.orders.installment-sales-contract',
          component: () => import('@/views/admin/orders/installmentSalesContract.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.installment_sales_contract'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/car-sales-contract',
          name: 'admin.orders.car-sales-contract',
          component: () => import('@/views/admin/orders/carSalesContract.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.car_sales_contract'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/tractor-sales-contract',
          name: 'admin.orders.tractor-sales-contract',
          component: () => import('@/views/admin/orders/tractorSalesContract.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.tractor_sales_contract'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/iyi-finans-mmks-contract',
          name: 'admin.orders.iyi-finans-mmks-contract',
          component: () => import('@/views/admin/orders/iyiFinansMmksContract.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.iyi_finans_mmks_contract'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/iyi-finans-promissory-note',
          name: 'admin.orders.iyi-finans-promissory-note',
          component: () => import('@/views/admin/orders/iyiFinansPromissoryNote.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.iyi_finans_promissory_note'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/sales-contract',
          name: 'admin.orders.sales-contract',
          component: () => import('@/views/admin/orders/salesContract.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.sales_contract'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/transportation-document',
          name: 'admin.orders.transportation-document',
          component: () => import('@/views/admin/orders/transportationDocument.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.transportation_document'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/repayment-schedule',
          name: 'admin.orders.repayment-schedule',
          component: () => import('@/views/admin/orders/repaymentSchedule.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.repayment_schedule'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/credit-agreement',
          name: 'admin.orders.credit-agreement',
          component: () => import('@/views/admin/orders/creditAgreement.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.credit_agreement'),
              parent: 'admin.orders.show',
              suspense: {
                enabled: true
              }
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/mortgage-contract-items/:itemId/view',
          name: 'admin.orders.mortgage-contract-items.view',
          component: () => import('@/views/admin/orders/mortgageContract.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.mortgage_contract'),
              parent: 'admin.orders.show',
              suspense: {
                enabled: true
              }
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/order-receipt',
          name: 'admin.orders.order-receipt',
          component: () => import('@/views/admin/orders/orderReceipt.vue'),
          props: true,
          meta: {
            breadcrumb: { name: 'Sipariş Fişi', parent: 'admin.orders.show' },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/fibabanka-documents/:type',
          name: 'admin.orders.fibabanka-documents',
          component: () => import('@/views/admin/orders/fibabankaDocument.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.detail'),
              parent: 'admin.orders.show'
            },
            permissions: ['view-order']
          }
        },
        {
          path: 'orders/:id/draft',
          name: 'admin.orders.draft',
          component: () => import('@/views/admin/orders/draft.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.detail'),
              parent: 'admin.orders.index',
              suspense: {
                enabled: true
              }
            },
            permissions: ['view-order']
          }
        },
        // endregion

        // region Farmer Documents
        {
          path: 'orders/:id/show-document/:documentId',
          name: 'admin.orders.show-document',
          component: () => import('@/views/admin/orders/showDocument.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: () => {
                const { document } = useOrderDocumentStore()
                return ORDER_DOCUMENT_TYPE_MAP[document?.type] ?? t('router.document_detail')
              },
              // Vehicle registration belongs to the application flow, so its breadcrumb points to
              // the application detail. Every other order document (e.g. promissory note) stays on
              // the sales/order detail. Falls back to the order detail when application_id is absent.
              parent: () => {
                const { document } = useOrderDocumentStore()
                const { hasAnyPermission } = useAdminAuthStore()
                const applicationId = document?.order?.application_id

                // The application detail requires view-(any-)application; without it the user would
                // see a link they cannot open, so fall back to the order detail in that case.
                if (
                  document?.type === ORDER_DOCUMENT_TYPE.VEHICLE_REGISTRATION &&
                  applicationId &&
                  hasAnyPermission(['view-any-application', 'view-application'])
                ) {
                  return { name: 'admin.applications.show', params: { id: applicationId } }
                }

                return { name: 'admin.orders.show', params: { id: document?.order?.id } }
              }
            },
            permissions: ['view-order-document']
          }
        },
        // endregion

        // region Suppliers
        {
          path: 'suppliers',
          name: 'admin.suppliers.index',
          component: () => import('@/views/admin/suppliers/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.suppliers') },
            permissions: ['view-any-supplier']
          }
        },
        {
          path: 'suppliers/:id',
          name: 'admin.suppliers.show',
          component: () => import('@/views/admin/suppliers/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.detail'),
              parent: 'admin.suppliers.index'
            },
            permissions: ['view-supplier']
          }
        },
        // endregion

        // region Farmers
        {
          path: 'farmers',
          name: 'admin.farmers.index',
          component: () => import('@/views/admin/farmers/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.farmers') },
            permissions: ['view-any-farmer', 'view-any-retailer']
          }
        },
        {
          path: 'farmers/:id',
          name: 'admin.farmers.show',
          component: () => import('@/views/admin/farmers/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.detail'),
              parent: 'admin.farmers.index'
            },
            permissions: ['view-farmer', 'view-any-order']
          }
        },
        {
          path: 'farmers/:id/edit',
          name: 'admin.farmers.edit',
          component: () => import('@/views/admin/farmers/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit_farmer'),
              parent: 'admin.farmers.show'
            },
            permissions: ['view-farmer', 'update-farmer']
          }
        },
        // endregion

        // region Farmer Documents
        {
          path: 'farmers/:id/view-document/:documentId',
          name: 'admin.farmers.view-document',
          component: () => import('@/views/admin/farmers/document.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: () => {
                const { document } = useFarmerDocumentStore()
                return FARMER_DOCUMENT_CATEGORY_MAP[document?.category] ?? t('router.document_detail')
              },
              // A farmer document can belong to many applications (many-to-many), so the origin
              // application id is carried on the link as a query param. When present, the breadcrumb
              // returns to that application detail; otherwise it falls back to the farmer detail.
              parent: route => {
                if (route.query.applicationId) {
                  return { name: 'admin.applications.show', params: { id: route.query.applicationId } }
                }

                return { name: 'admin.farmers.show', params: { id: route.params.id } }
              }
            },
            permissions: ['view-farmer-document']
          }
        },
        {
          path: 'farmers/:id/show-document/:documentId',
          name: 'admin.farmers.show-document',
          component: () => import('@/views/admin/farmers/showDocument.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: () => {
                const { document } = useFarmerDocumentStore()
                return FARMER_DOCUMENT_CATEGORY_MAP[document?.category] ?? t('router.document_detail')
              },
              // A farmer document can belong to many applications (many-to-many), so the origin
              // application id is carried on the link as a query param. When present, the breadcrumb
              // returns to that application detail; otherwise it falls back to the farmer detail.
              parent: route => {
                if (route.query.applicationId) {
                  return { name: 'admin.applications.show', params: { id: route.query.applicationId } }
                }

                return { name: 'admin.farmers.show', params: { id: route.params.id } }
              }
            },
            permissions: ['view-farmer-document']
          }
        },
        // endregion

        // region Mobile Contacts
        {
          path: 'mobile-contacts',
          name: 'admin.mobile-contacts.index',
          component: () => import('@/views/admin/mobileContacts/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.mobile_contacts') },
            permissions: ['view-any-mobile-contact']
          }
        },
        {
          path: 'mobile-contacts/:id',
          name: 'admin.mobile-contacts.show',
          component: () => import('@/views/admin/mobileContacts/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.detail'),
              parent: 'admin.mobile-contacts.index'
            },
            permissions: ['view-mobile-contact']
          }
        },
        // endregion

        // region Mobile Banners
        {
          path: 'mobile-banners',
          name: 'admin.mobile-banners.index',
          component: () => import('@/views/admin/mobileBanners/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.mobile_banners'),
              parentLabel: t('views.layouts.marketing')
            },
            permissions: ['view-any-mobile-banner']
          }
        },
        {
          path: 'mobile-banners/create',
          name: 'admin.mobile-banners.create',
          component: () => import('@/views/admin/mobileBanners/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add_mobile_banner'),
              parent: 'admin.mobile-banners.index'
            },
            permissions: ['create-mobile-banner']
          }
        },
        {
          path: 'mobile-banners/:id',
          name: 'admin.mobile-banners.show',
          component: () => import('@/views/admin/mobileBanners/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.detail'),
              parent: 'admin.mobile-banners.index'
            },
            permissions: ['view-mobile-banner']
          }
        },
        {
          path: 'mobile-banners/:id/edit',
          name: 'admin.mobile-banners.edit',
          component: () => import('@/views/admin/mobileBanners/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.detail'),
              parent: 'admin.mobile-banners.index'
            },
            hideTitle: true,
            permissions: ['view-mobile-banner', 'update-mobile-banner']
          }
        },
        // endregion

        // region Order Items
        {
          path: 'order-items',
          name: 'admin.order-items.index',
          component: () => import('@/views/admin/orderItems/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.order_items') }
          }
        },
        // endregion

        // region Farm Runner Game
        {
          path: 'farm-runners',
          name: 'admin.farm-runners.index',
          component: () => import('@/views/admin/farmRunners/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.farm_ranners') }
          }
        },
        // endregion

        // region Admin Settings
        {
          path: 'settings',
          name: 'admin.settings.index',
          component: () => import('@/views/admin/settings/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.settings') },
            permissions: ['view-any-setting']
          }
        },
        {
          path: 'settings/create',
          name: 'admin.settings.create',
          component: () => import('@/views/admin/settings/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.settings.index'
            },
            permissions: ['view-any-setting']
          }
        },
        {
          path: 'settings/:id/edit',
          name: 'admin.settings.edit',
          component: () => import('@/views/admin/settings/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit_settings'),
              parent: 'admin.settings.index'
            },
            permissions: ['view-any-setting']
          }
        },
        // endregion

        // region Admin System Notifications
        {
          path: 'system-notifications',
          name: 'admin.system-notifications.index',
          component: () => import('@/views/admin/systemNotifications/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.system_notifications') },
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'system-notifications/create',
          name: 'admin.system-notifications.create',
          component: () => import('@/views/admin/systemNotifications/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.system-notifications.index'
            },
            suspense: {
              enabled: true
            }
          }
        },
        // endregion

        // region Workflows
        {
          path: 'workflows',
          name: 'admin.workflows.index',
          component: () => import('@/views/admin/workflows/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.order_workflows') },
            permissions: ['view-any-workflow-job']
          }
        },
        {
          path: 'workflows/create',
          name: 'admin.workflows.create',
          component: () => import('@/views/admin/workflows/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.workflows.index'
            },
            permissions: ['create-workflow-job']
          }
        },
        {
          path: 'workflows/:id/edit',
          name: 'admin.workflows.edit',
          component: () => import('@/views/admin/workflows/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit_settings'),
              parent: 'admin.workflows.index'
            },
            permissions: ['update-workflow-job']
          }
        },
        // endregion

        // region Phone Surveys
        {
          path: 'phone-surveys',
          name: 'admin.phone-surveys.index',
          component: () => import('@/views/admin/phoneSurveys/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.phone_surveys') },
            permissions: ['view-any-phone-survey']
          }
        },
        {
          path: 'phone-surveys/:id',
          name: 'admin.phone-surveys.show',
          component: () => import('@/views/admin/phoneSurveys/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.phone_survey'),
              parent: 'admin.phone-surveys.index'
            }
          }
        },
        {
          path: 'phone-surveys/:id/create',
          name: 'admin.phone-surveys.create',
          component: () => import('@/views/admin/phoneSurveys/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.phone-surveys.index'
            }
          }
        },
        {
          path: 'phone-surveys/:phone_survey_type/questions/:question_id/edit',
          name: 'admin.phone-surveys.edit',
          component: () => import('@/views/admin/phoneSurveys/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit_phone_surveys'),
              parent: 'admin.phone-surveys.index'
            }
          }
        },
        // endregion

        // region Ranks
        {
          path: 'ranks',
          name: 'admin.ranks.index',
          component: () => import('@/views/admin/ranks/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.retailer_ranks') },
            permissions: ['view-any-rank']
          }
        },
        {
          path: 'ranks/create',
          name: 'admin.ranks.create',
          component: () => import('@/views/admin/ranks/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.ranks.index'
            },
            permissions: ['create-rank']
          }
        },
        {
          path: 'ranks/:rank_id/edit',
          name: 'admin.ranks.edit',
          component: () => import('@/views/admin/ranks/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.ranks.index'
            },
            permissions: ['update-rank']
          }
        },
        // endregion

        // region Goals
        {
          path: 'ranks/:rank_id/goals',
          name: 'admin.ranks.goals.index',
          component: () => import('@/views/admin/goals/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.goals'),
              parent: 'admin.ranks.index'
            },
            permissions: ['view-any-goal']
          }
        },
        {
          path: 'ranks/:rank_id/goals/create',
          name: 'admin.ranks.goals.create',
          component: () => import('@/views/admin/goals/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.ranks.goals.index'
            },
            permissions: ['create-goal']
          }
        },
        {
          path: 'ranks/:rank_id/goals/:goal_id/edit',
          name: 'admin.ranks.goals.edit',
          component: () => import('@/views/admin/goals/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.ranks.goals.index'
            },
            permissions: ['update-goal']
          }
        },
        {
          path: 'ranks/:rank_id/goals/:goal_id/retailers',
          name: 'admin.ranks.goals.retailers.index',
          component: () => import('@/views/admin/goals/retailers.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.retailers_has_goal'),
              parent: 'admin.ranks.goals.index'
            },
            permissions: ['view-goal']
          }
        },
        // endregion

        // region Goal Requirements
        {
          path: 'ranks/:rank_id/goals/:goal_id/requirements',
          name: 'admin.ranks.goals.requirements.index',
          component: () => import('@/views/admin/goals/requirements/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.goal_requirements'),
              parent: 'admin.ranks.goals.index'
            },
            permissions: ['view-any-goal-requirements']
          }
        },

        // endregion

        // region Achievements
        {
          path: 'ranks/:rank_id/goals/:goal_id/achievements',
          name: 'admin.ranks.goals.achievements.index',
          component: () => import('@/views/admin/achievements/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.goal_achievements'),
              parent: 'admin.ranks.goals.index'
            },
            permissions: ['view-any-achievement']
          }
        },
        {
          path: 'ranks/:rank_id/goals/:goal_id/achievements/create',
          name: 'admin.ranks.goals.achievements.create',
          component: () => import('@/views/admin/achievements/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.ranks.goals.achievements.index'
            },
            permissions: ['create-achievement']
          }
        },
        {
          path: 'ranks/:rank_id/goals/:goal_id/achievements/:achievement_id/edit',
          name: 'admin.ranks.goals.achievements.edit',
          component: () => import('@/views/admin/achievements/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.ranks.goals.achievements.index'
            },
            permissions: ['update-achievement']
          }
        },
        // endregion

        // region Rewards
        {
          path: 'rewards',
          name: 'admin.rewards.index',
          component: () => import('@/views/admin/rewards/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.rewards') },
            permissions: ['view-any-reward']
          }
        },
        {
          path: 'rewards/create',
          name: 'admin.rewards.create',
          component: () => import('@/views/admin/rewards/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.rewards.index'
            },
            permissions: ['create-reward']
          }
        },
        {
          path: 'rewards/:id/edit',
          name: 'admin.rewards.edit',
          component: () => import('@/views/admin/rewards/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.rewards.index'
            },
            permissions: ['update-reward']
          }
        },
        // endregion

        // region Order Documents
        {
          path: 'documents',
          name: 'admin.documents.index',
          component: () => import('@/views/admin/documents/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.uploaded_documents') },
            permissions: ['view-any-document']
          }
        },
        {
          path: 'documents/signed-promissory-note',
          name: 'admin.documents.signedPromissoryNote.index',
          component: () => import('@/views/admin/documents/signedPromissoryNote/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.upload_document') },
            permissions: ['create-document']
          }
        },
        {
          path: 'documents/cancelled-promissory-note',
          name: 'admin.documents.cancelledPromissoryNote.index',
          component: () => import('@/views/admin/documents/cancelledPromissoryNote/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.upload_document') },
            permissions: ['create-document']
          }
        },
        {
          path: 'documents/identity',
          name: 'admin.documents.identity.index',
          component: () => import('@/views/admin/documents/identity/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.upload_document') },
            permissions: ['create-document']
          }
        },
        {
          path: 'documents/:document/processes',
          name: 'admin.documents.processes.index',
          component: () => import('@/views/admin/documentProcesses/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.document_processes'),
              parent: 'admin.documents.index'
            },
            permissions: ['view-any-document-process']
          }
        },
        {
          path: 'documents/:document/processes/:process',
          name: 'admin.documents.processes.show',
          component: () => import('@/views/admin/documentProcesses/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.documents'),
              parent: 'admin.documents.processes.index'
            },
            permissions: ['view-document-process']
          }
        },
        {
          path: 'documents/:document/control-processes/:process',
          name: 'admin.document.control-processes.show',
          component: () => import('@/views/admin/documentControlProcesses/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.documents'),
              parent: 'admin.documents.document-control-processes.index'
            },
            permissions: ['view-document-process']
          }
        },
        {
          path: 'document-control-process/upload',
          name: 'admin.documents.document-control-processes.upload',
          component: () => import('@/views/admin/documentControlProcesses/upload.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.upload_document') },
            permissions: ['create-document']
          }
        },
        {
          path: 'documents/:document/control-processes',
          name: 'admin.documents.document-control-processes.index',
          component: () => import('@/views/admin/documentControlProcesses/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.document_processes'),
              parent: 'admin.documents.index'
            },
            permissions: ['view-any-document-process']
          }
        },
        // endregion

        // region Crops
        {
          path: 'crops',
          name: 'admin.crops.index',
          component: () => import('@/views/admin/crops/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.crops') },
            permissions: ['view-any-crops']
          }
        },
        {
          path: 'crops/create',
          name: 'admin.crops.create',
          component: () => import('@/views/admin/crops/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.crops.index'
            },
            permissions: ['create-crop']
          }
        },
        {
          path: 'crops/:id/edit',
          name: 'admin.crops.edit',
          component: () => import('@/views/admin/crops/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.crops.index'
            },
            permissions: ['update-crop']
          }
        },
        // endregion

        // region Crop Price Indices
        {
          path: 'crop-price-indices',
          name: 'admin.crop-price-indices.index',
          component: () => import('@/views/admin/cropPriceIndices/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.crop-price-indices') },
            permissions: ['view-any-crop-price-index']
          }
        },
        {
          path: 'crop-price-indices/create',
          name: 'admin.crop-price-indices.create',
          component: () => import('@/views/admin/cropPriceIndices/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.crop-price-indices.index'
            },
            permissions: ['create-crop-price-index']
          }
        },
        // endregion

        // region Contacts
        {
          path: 'contacts',
          name: 'admin.contacts.index',
          component: () => import('@/views/admin/contacts/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.contacts') },
            permissions: ['view-any-contact']
          }
        },
        // endregion

        // region Blacklists
        {
          path: 'blacklists',
          name: 'admin.blacklists.index',
          component: () => import('@/views/admin/blacklists/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.blacklist') },
            permissions: ['view-any-blacklist']
          }
        },
        {
          path: 'blacklists/create',
          name: 'admin.blacklists.create',
          component: () => import('@/views/admin/blacklists/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.blacklists.index'
            },
            permissions: ['create-blacklist']
          }
        },
        {
          path: 'blacklists/:id',
          name: 'admin.blacklists.show',
          component: () => import('@/views/admin/blacklists/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.detail'),
              parent: 'admin.blacklists.index'
            },
            permissions: ['view-blacklist']
          }
        },
        {
          path: 'blacklists/:id/edit',
          name: 'admin.blacklists.edit',
          component: () => import('@/views/admin/blacklists/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.blacklists.index'
            },
            permissions: ['update-blacklist']
          }
        },
        // endregion

        // region Payment Reminders
        {
          path: 'payment-reminders',
          name: 'admin.payment-reminders.index',
          component: () => import('@/views/admin/paymentReminders/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.payment_reminders') },
            permissions: ['view-any-payment-reminder']
          }
        },
        // endregion

        // region Payment Reminders
        {
          path: 'administrative-proceeding-calls',
          name: 'admin.administrative-proceeding-calls.index',
          component: () => import('@/views/admin/administrativeProceedingReminders/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.administrative_proceeding_calls') },
            permissions: ['view-any-administrative-proceeding-reminder'],
            suspense: {
              enabled: true
            }
          }
        },
        // endregion

        // region Delivery Confirmation
        {
          path: 'delivery-reminders',
          name: 'admin.delivery-reminders.index',
          component: () => import('@/views/admin/deliveryReminders/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.delivery_reminder') },
            permissions: ['view-any-delivery-reminder']
          }
        },
        // endregion

        // region Brands
        {
          path: 'brands',
          name: 'admin.brands.index',
          component: () => import('@/views/admin/brands/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.brands') },
            permissions: ['view-any-brand']
          }
        },
        {
          path: 'brands/create',
          name: 'admin.brands.create',
          component: () => import('@/views/admin/brands/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add_brand'),
              parent: 'admin.brands.index'
            },
            permissions: ['create-brand']
          }
        },
        {
          path: 'brands/:id/edit',
          name: 'admin.brands.edit',
          component: () => import('@/views/admin/brands/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit_brand'),
              parent: 'admin.brands.index'
            },
            permissions: ['update-brand']
          }
        },
        // endregion

        // region Posts
        {
          path: 'posts',
          name: 'admin.posts.index',
          component: () => import('@/views/admin/posts/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.posts') },
            permissions: ['view-any-post']
          }
        },
        {
          path: 'posts/create',
          name: 'admin.posts.create',
          component: () => import('@/views/admin/posts/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.posts.index'
            },
            permissions: ['create-post']
          }
        },
        {
          path: 'posts/:post/edit',
          name: 'admin.posts.edit',
          component: () => import('@/views/admin/posts/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.posts.index'
            },
            permissions: ['update-post']
          }
        },
        // endregion

        // region JobBoards
        {
          path: 'job-boards',
          name: 'admin.job-boards.index',
          component: () => import('@/views/admin/jobBoard/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('views.layouts.job_boards') },
            suspense: {
              enabled: true
            },
            permissions: ['view-any-job-board']
          }
        },
        {
          path: 'job-boards/create',
          name: 'admin.job-boards.create',
          component: () => import('@/views/admin/jobBoard/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'admin.job-boards.index'
            },
            permissions: ['create-job-board'],
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'job-boards/:jobBoard/edit',
          name: 'admin.job-boards.edit',
          component: () => import('@/views/admin/jobBoard/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'admin.job-boards.index'
            },
            permissions: ['update-job-board'],
            suspense: {
              enabled: true
            }
          }
        },
        // endregion

        // region Suggested Products

        {
          path: 'suggested-products',
          name: 'admin.suggested-products.index',
          component: () => import('@/views/admin/suggestedProducts/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.suggested_products') },
            permissions: ['view-any-suggested-product']
          }
        },

        // endregion

        // region Retailer Stock Inputs
        {
          path: 'retailer-short-sales',
          name: 'admin.retailer-short-sales.index',
          component: () => import('@/views/admin/retailerShortSales/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.short_sales') },
            permissions: ['view-any-retailer-short-sale']
          }
        },
        {
          path: 'retailer-stock-inputs',
          name: 'admin.retailer-stock-inputs.index',
          component: () => import('@/views/admin/retailerStockInputs/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.stock_inputs') },
            permissions: ['view-any-retailer-stock-input']
          }
        },
        {
          path: 'retailer-stock-inputs/:id/edit',
          name: 'admin.retailer-stock-inputs.edit',
          component: () => import('@/views/admin/retailerStockInputs/edit.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.update_stock_inputs') },
            permissions: ['update-retailer-stock-input']
          }
        },
        {
          path: 'retailer-stock-inputs/create',
          name: 'admin.retailer-stock-inputs.create',
          component: () => import('@/views/admin/retailerStockInputs/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add_stock_input'),
              parent: 'admin.retailer-stock-inputs.index'
            },
            permissions: ['create-retailer-stock-input']
          }
        },
        {
          path: 'retailer-stock-inputs/:id',
          name: 'admin.retailer-stock-inputs.show',
          component: () => import('@/views/admin/retailerStockInputs/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.stock_input_details'),
              parent: 'admin.retailer-stock-inputs.index'
            },
            permissions: ['view-retailer-stock-input']
          }
        },
        {
          path: 'tarfin-pro-stock-interests',
          name: 'admin.tarfin-pro-stock-interests.index',
          component: () => import('@/views/admin/tarfinProStockInterests/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.tarfin_pro_stock_interest') },
            permissions: ['view-any-tarfin-pro-stock-interest'],
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'tarfin-pro-campaigns',
          name: 'admin.tarfin-pro-campaigns.index',
          component: () => import('@/views/admin/tarfinProCampaigns/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.tarfin_pro_campaigns') },
            permissions: ['view-any-tarfin-pro-campaign'],
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'tarfin-pro-campaigns/create',
          name: 'admin.tarfin-pro-campaigns.create',
          component: () => import('@/views/admin/tarfinProCampaigns/create.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.tarfin_pro_campaigns') },
            permissions: ['create-tarfin-pro-campaign']
          }
        },
        {
          path: 'tarfin-pro-campaigns/:id/edit',
          name: 'admin.tarfin-pro-campaigns.edit',
          component: () => import('@/views/admin/tarfinProCampaigns/edit.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.tarfin_pro_campaigns_edit') },
            permissions: ['update-tarfin-pro-campaign']
          }
        },
        {
          path: 'tarfin-pro-campaigns/:id',
          name: 'admin.tarfin-pro-campaigns.show',
          component: () => import('@/views/admin/tarfinProStockInterests/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.tarfin_pro_stock_interest') },
            permissions: ['view-any-stock-interest', 'view-tarfin-pro-campaign'],
            suspense: {
              enabled: true
            }
          }
        },
        // endregion

        // region Tarfin Pro Product Price
        {
          path: 'tarfin-pro-product-prices',
          name: 'admin.tarfin-pro-product-price.index',
          component: () => import('@/views/admin/tarfinProProductPrices/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('views.layouts.tarfin_pro_product_price')
            },
            permissions: 'view-any-tarfin-pro-product-price'
          }
        },
        // endregion

        // region Current Account
        {
          path: 'retailers/:id/current-account/activities',
          name: 'admin.retailers.current-account.activities.index',
          component: () => import('@/views/admin/currentAccountActivities/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.transactions'),
              parent: 'admin.retailers.show'
            }
          }
        },
        {
          path: 'purchase-requests',
          name: 'admin.purchase-requests.index',
          component: () => import('@/views/admin/purchaseRequests/index.vue'),
          meta: {
            breadcrumb: { name: t('router.purchase_requests') },
            permissions: ['view-any-purchase-request']
          }
        },
        {
          path: 'purchase-requests/:id',
          name: 'admin.purchase-requests.show',
          component: () => import('@/views/admin/purchaseRequests/show.vue'),
          meta: {
            breadcrumb: {
              name: t('router.purchase_request_detail'),
              parent: 'admin.purchase-requests.index'
            },
            permissions: ['view-purchase-request']
          }
        },
        // endregion

        // region for Service Errors
        {
          path: 'service-errors',
          name: 'admin.service-errors.index',
          component: () => import('@/views/admin/serviceErrors/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('views.layouts.service_errors')
            }
          }
        },
        {
          path: 'service-errors/:service_error',
          name: 'admin.service-errors.show',
          component: () => import('@/views/admin/serviceErrors/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.service_error'),
              parent: 'admin.service-errors.index'
            }
          }
        },
        // endregion for Service Errors

        // region - PreApproved Limit Applications
        {
          path: 'pre-approved-limit-applications',
          name: 'admin.pre-approved-limit-applications.index',
          component: () => import('@/views/admin/preApprovedLimitApplications/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('views.layouts.pre_approved_limit_applications')
            }
          }
        },
        {
          path: 'pre-approved-limit-applications/:id',
          name: 'admin.pre-approved-limit-applications.show',
          component: () => import('@/views/admin/preApprovedLimitApplications/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('views.layouts.pre_approved_limit_application'),
              parent: 'admin.pre-approved-limit-applications.index'
            }
          }
        },
        {
          path: 'code-refactoring-map',
          name: 'admin.codeRefactoringMap.index',
          component: () => import('@/views/admin/codeRefactoringMap/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: 'Kod Düzenleme Haritası'
            }
          }
        },
        // endregion

        {
          path: 'bulk-farmer-limit-applications/create',
          name: 'admin.bulk-farmer-limit-applications.create',
          component: () => import('@/views/admin/bulkFarmerLimitApplications/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('views.layouts.create_bulk_farmer_limit_application'),
              parent: 'admin.bulk-farmer-limit-applications.index'
            }
          }
        },
        {
          path: 'bulk-farmer-limit-applications',
          name: 'admin.bulk-farmer-limit-applications.index',
          component: () => import('@/views/admin/bulkFarmerLimitApplications/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('views.layouts.bulk_farmer_limit_applications')
            }
          }
        },
        {
          path: 'bulk-farmer-limit-applications/:id',
          name: 'admin.bulk-farmer-limit-applications.show',
          component: () => import('@/views/admin/bulkFarmerLimitApplications/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('views.layouts.bulk_farmer_limit_application'),
              parent: 'admin.bulk-farmer-limit-applications.index'
            }
          }
        },

        {
          path: 'logout',
          name: 'admin-logout'
        }
      ]
    },
    {
      path: '/retailer',
      component: Panel,
      beforeEnter: [
        retailerRequireAuth,
        retailerSalesChannelControl,
        retailerPolicyControl,
        retailerConsumerGoodsBlocker,
        retailerClosedSalesChannelBlocker
      ],
      meta: {
        breadcrumb: { name: t('router.panel') }
      },
      children: [
        {
          path: 'ro',
          component: Romania,
          meta: {
            breadcrumb: { name: t('router.go_to_romania') }
          },
          children: ro
        },
        {
          path: '',
          name: 'retailer.dashboard',
          component: () => import('@/views/retailer/dashboard/dashboard.vue'),
          meta: {
            breadcrumb: { name: t('router.dashboard') },
            allowForAllRetailerTypes: true,
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'notifications',
          name: 'retailer.notifications',
          component: () => import('@/views/retailer/notifications/index.vue'),
          meta: {
            breadcrumb: { name: t('router.notifications') },
            allowForAllRetailerTypes: true
          }
        },

        // region Account
        {
          path: 'account/userEdit',
          name: 'retailer.account.userEdit',
          component: () => import('@/views/retailer/account/userEdit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'retailer.account'
            },
            allowForAllRetailerTypes: true
          }
        },
        {
          path: 'account',
          name: 'retailer.account',
          component: () => import('@/views/retailer/account/show.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.account') },
            allowForAllRetailerTypes: true
          }
        },
        {
          path: 'account/edit',
          beforeEnter: checkCountryAccess,
          name: 'retailer.account.edit',
          component: () => import('@/views/retailer/account/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'retailer.account'
            },
            countryAccess: {
              allowed: 'ro',
              redirect: 'retailer.account'
            }
          }
        },
        // endregion

        // region Orders
        {
          path: 'orders',
          name: 'retailer.orders.index',
          component: () => import('@/views/retailer/orders/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.orders') },
            allowForAllRetailerTypes: true,
            allowWithoutSalesChannels: retailer => retailer?.activated_at
          }
        },
        {
          path: 'orders/:id',
          name: 'retailer.orders.show',
          component: () => import('@/views/retailer/orders/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.order_detail'),
              parent: 'retailer.orders.index'
            },
            allowForAllRetailerTypes: true
          }
        },
        {
          path: '404',
          name: 'retailer.404',
          component: () => import('@/views/retailer/errors/notFound.vue'),
          meta: {
            breadcrumb: { name: t('views.pages.page_not_found') },
            allowForAllRetailerTypes: true,
            allowWithoutSalesChannels: () => true
          }
        },
        {
          path: 'orders/:id/pay-with-credit-card',
          name: 'retailer.orders.credit-card-payment',
          component: () => import('@/views/retailer/creditCardPayment/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.credit_card_payment'),
              parent: 'retailer.orders.show'
            },
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'orders/:id/credit-card-payment-success',
          name: 'retailer.orders.credit-card-sucess',
          component: () => import('@/views/retailer/creditCardPayment/success.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.credit_card_payment'),
              parent: 'retailer.orders.index'
            },
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'orders/:id/invoice',
          name: 'retailer.orders.invoice',
          component: () => import('@/views/retailer/orders/invoice.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.invoice'),
              parent: 'retailer.orders.show'
            },
            allowForAllRetailerTypes: true
          }
        },
        {
          path: 'orders/:id/farmer-transportation-document',
          name: 'retailer.orders.farmer-transportation-document',
          component: () => import('@/views/retailer/orders/farmerTransportationDocument.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('views.retailer.orders.farmer_transportation_document'),
              parent: 'retailer.orders.show'
            }
          }
        },
        {
          path: 'orders/:id/promissory-note',
          name: 'retailer.orders.promissory-note',
          component: () => import('@/views/retailer/orders/promissoryNote.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.promissory_note'),
              parent: 'retailer.orders.show'
            }
          }
        },
        {
          path: 'orders/:id/car-sales-installment-promissory-note',
          name: 'retailer.orders.car-sales-installment-promissory-note',
          component: () => import('@/views/retailer/orders/carSalesInstallmentPromissoryNote.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.car_sales_installment_promissory_note'),
              parent: 'retailer.orders.show'
            },
            allowForCarSales: true
          }
        },
        {
          path: 'orders/:id/tractor-sales-installment-promissory-note',
          name: 'retailer.orders.tractor-sales-installment-promissory-note',
          component: () => import('@/views/retailer/orders/tractorSalesInstallmentPromissoryNote.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.tractor_sales_installment_promissory_note'),
              parent: 'retailer.orders.show'
            },
            allowForTractorSales: true
          }
        },
        {
          path: 'orders/:id/sales-contract',
          name: 'retailer.orders.sales-contract',
          component: () => import('@/views/retailer/orders/salesContract.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.sales_contract'),
              parent: 'retailer.orders.show'
            }
          }
        },
        {
          path: 'orders/:id/installment-sales-contract',
          name: 'retailer.orders.installment-sales-contract',
          component: () => import('@/views/retailer/orders/installmentSalesContract.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.installment_sales_contract'),
              parent: 'retailer.orders.show'
            },
            allowForConsumerGoods: true
          }
        },
        {
          path: 'orders/:id/vehicle-registration',
          name: 'retailer.orders.vehicle-registration',
          component: () => import('@/views/retailer/orders/vehicleRegistration.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.vehicle_registration'),
              parent: 'retailer.orders.show'
            },
            allowForCarSales: true
          }
        },
        {
          path: 'orders/:id/car-sales-contract',
          name: 'retailer.orders.car-sales-contract',
          component: () => import('@/views/retailer/orders/carSalesContract.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.car_sales_contract'),
              parent: 'retailer.orders.show'
            },
            allowForCarSales: true
          }
        },
        {
          path: 'orders/:id/tractor-sales-contract',
          name: 'retailer.orders.tractor-sales-contract',
          component: () => import('@/views/retailer/orders/tractorSalesContract.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.tractor_sales_contract'),
              parent: 'retailer.orders.show'
            },
            allowForTractorSales: true
          }
        },
        {
          path: 'orders/:id/iyi-finans-mmks-contract',
          name: 'retailer.orders.iyi-finans-mmks-contract',
          component: () => import('@/views/retailer/orders/iyiFinansMmksContract.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.iyi_finans_mmks_contract'),
              parent: 'retailer.orders.show'
            },
            allowForIyiFinans: true
          }
        },
        {
          path: 'orders/:id/iyi-finans-promissory-note',
          name: 'retailer.orders.iyi-finans-promissory-note',
          component: () => import('@/views/retailer/orders/iyiFinansPromissoryNote.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.iyi_finans_promissory_note'),
              parent: 'retailer.orders.show'
            },
            allowForIyiFinans: true
          }
        },
        {
          path: 'orders/:id/transportation-document',
          name: 'retailer.orders.transportation-document',
          component: () => import('@/views/retailer/orders/transportationDocument.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.transportation_document'),
              parent: 'retailer.orders.show'
            }
          }
        },
        {
          path: 'orders/:id/repayment-schedule',
          name: 'retailer.orders.repayment-schedule',
          component: () => import('@/views/retailer/orders/repaymentSchedule.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.repayment_schedule'),
              parent: 'retailer.orders.show'
            }
          }
        },
        {
          path: 'orders/:id/credit-agreement',
          name: 'retailer.orders.credit-agreement',
          component: () => import('@/views/retailer/orders/creditAgreement.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.credit_agreement'),
              parent: 'retailer.orders.show'
            },
            suspense: {
              enabled: true
            }
          }
        },
        {
          path: 'orders/:id/credit-application',
          name: 'retailer.orders.credit-application',
          component: () => import('@/views/retailer/orders/creditApplication.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.credit_application'),
              parent: 'retailer.orders.show'
            }
          }
        },
        {
          path: 'orders/:id/order-receipt',
          name: 'retailer.orders.order-receipt',
          component: () => import('@/views/retailer/orders/orderReceipt.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.order_receipt'),
              parent: 'retailer.orders.show'
            }
          }
        },
        {
          path: 'orders/:id/delivery-and-confirmation-record',
          name: 'retailer.orders.delivery-and-confirmation-record',
          component: () => import('@/views/retailer/orders/deliveryAndConfirmationRecord.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.delivery_and_confirmation_record'),
              parent: 'retailer.orders.show'
            }
          }
        },
        {
          path: 'orders/:id/crop-equivalent-contract',
          name: 'retailer.orders.crop-equivalent-contract',
          component: () => import('@/views/retailer/orders/cropEquivalentContract.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.crop_equivalent_contract'),
              parent: 'retailer.orders.show'
            }
          }
        },
        {
          path: 'orders/:id/fibabank-documents/:type',
          name: 'retailer.orders.fibabank-documents',
          component: () => import('@/views/retailer/orders/fibaDocuments.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.detail'), parent: 'retailer.orders.show' }
          }
        },
        {
          path: 'orders/:id/promissory-note-and-order-receipt',
          name: 'retailer.orders.promissory-note-and-order-receipt',
          component: () => import('@/views/retailer/orders/promissoryNoteAndOrderReceipt.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.detail'), parent: 'retailer.orders.show' }
          }
        },
        {
          path: 'orders/:id/mortgage-contract-items/:itemId',
          name: 'retailer.orders.mortgage-contract-items.view',
          component: () => import('@/views/retailer/orders/mortgageContract.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.detail'), parent: 'retailer.orders.show' }
          }
        },
        // endregion

        // region Farmers
        {
          path: 'farmers',
          name: 'retailer.farmers.index',
          component: () => import('@/views/retailer/farmers/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.farmers') },
            allowForConsumerGoods: false,
            allowWithoutSalesChannels: () => true
          }
        },
        {
          path: 'farmers',
          name: 'retailer.customers.index',
          component: () => import('@/views/retailer/farmers/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.customers') },
            allowForAllRetailerTypes: true,
            allowWithoutSalesChannels: () => true
          }
        },
        {
          path: 'farmers/:id',
          name: 'retailer.farmers.show',
          component: () => import('@/views/retailer/farmers/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.detail'),
              parent: 'retailer.farmers.index'
            },
            allowForConsumerGoods: true,
            allowForCarSales: true,
            allowForTractorSales: true
          }
        },
        // endregion

        // region Sub Retailers
        {
          path: 'sub-retailers',
          name: 'retailer.sub-retailers.index',
          component: () => import('@/views/retailer/subRetailers/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.sub_retailers') }
          }
        },
        {
          path: 'sub-retailers/:id',
          name: 'retailer.sub-retailers.show',
          component: () => import('@/views/retailer/subRetailers/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.sub_retailer'),
              parent: 'retailer.sub-retailers.index'
            }
          }
        },
        // endregion

        // region Users
        {
          path: 'users',
          name: 'retailer.users.index',
          component: () => import('@/views/retailer/users/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.users') },
            allowForAllRetailerTypes: true,
            allowWithoutSalesChannels: () => true
          }
        },
        {
          path: 'support',
          name: 'retailer.support.index',
          component: () => import('@/views/retailer/support/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.support_team') },
            permissions: ['view-any-user'],
            allowWithoutSalesChannels: () => true
          }
        },
        {
          path: 'users/create',
          name: 'retailer.users.create',
          component: () => import('@/views/retailer/users/create.vue'),
          meta: {
            breadcrumb: {
              name: t('router.add'),
              parent: 'retailer.users.index'
            },
            allowForAllRetailerTypes: true
          }
        },
        {
          path: 'users/:id/edit',
          name: 'retailer.users.edit',
          component: () => import('@/views/retailer/users/edit.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.edit'),
              parent: 'retailer.users.index'
            },
            allowForAllRetailerTypes: true
          }
        },
        // endregion

        // region Account Information
        {
          path: 'information',
          name: 'retailer.information',
          component: () => import('@/views/pages/Information.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.dashboard') },
            // Every retailer type: retailerSalesChannelControl redirects here when the retailer has
            // no sales channels, so blocking a type would trap it in a redirect loop.
            allowForAllRetailerTypes: true
          }
        },
        // endregion

        // region Policy Acceptance
        {
          path: 'policy',
          name: 'retailer.policy',
          component: () => import('@/views/pages/Policy.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('views.pages.privacy_policy_text') },
            // Every retailer type: retailerPolicyControl redirects here until the policy is
            // accepted, so blocking a type would trap it in a redirect loop.
            allowForAllRetailerTypes: true
          }
        },
        // endregion

        // region No Forward Sales
        {
          path: 'no-forward-sales',
          name: 'retailer.no-forward-sales',
          component: () => import('@/views/pages/NoForwardSale.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('views.pages.no_sales_channel_active') },
            suspense: {
              enabled: true
            }
          }
        },
        // endregion

        // region Car Campaigns
        {
          path: 'car-campaigns',
          name: 'retailer.car-campaigns.index',
          component: () => import('@/views/retailer/carCampaigns/index.vue'),
          meta: {
            breadcrumb: { name: t('router.car_campaigns') },
            hideTitle: true
          }
        },
        // endregion

        // region Goals
        {
          path: 'goals',
          name: 'retailer.goals.index',
          component: () => import('@/views/retailer/goals/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.goals') }
          }
        },
        // endregion

        // region Prices
        {
          path: 'prices',
          name: 'retailer.prices.index',
          component: () => import('@/views/retailer/prices/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.prices') }
          }
        },
        // endregion

        // region Tarfin Company Info
        {
          path: 'tarfin-company-info',
          name: 'retailer.tarfin.company.info',
          component: () => import('@/views/retailer/tarfin/info.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: () =>
                Retailer.isCarSales() === true
                  ? t('router.car_sales_company_information')
                  : t('router.company_information')
            },
            allowForAllRetailerTypes: true,
            allowWithoutSalesChannels: () => true
          }
        },
        // endregion

        // region Mobile Contacts
        {
          path: 'mobile-contacts/',
          name: 'retailer.mobile-contacts.index',
          component: () => import('@/views/retailer/mobileContacts/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.mobile_applications') }
          }
        },
        {
          path: 'mobile-contacts/:id',
          name: 'retailer.mobile-contacts.show',
          component: () => import('@/views/retailer/mobileContacts/show.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.mobile_contact'),
              parent: 'retailer.mobile-contacts.index'
            }
          }
        },
        // endregion

        // region Brands
        {
          path: 'brands',
          name: 'retailer.brands.index',
          component: () => import('@/views/retailer/brands/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.brands') }
          }
        },
        {
          path: 'brands/create',
          name: 'retailer.brands.create',
          component: () => import('@/views/retailer/brands/create.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.add_brand'),
              parent: 'retailer.brands.index'
            }
          }
        },
        // endregion

        // region FAQ
        {
          path: 'faq',
          name: 'retailer.faq.index',
          component: () => import('@/views/retailer/faq/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.faq') },
            allowWithoutSalesChannels: () => true
          }
        },
        // endregion

        // region Ro FAQ
        {
          path: 'romanian_faq',
          name: 'retailer.ro.faq.index',
          component: () => import('@/views/retailer/faq/international/romania/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: t('router.faq_ro') },
            suspense: {
              enabled: true
            }
          }
        },
        // endregion

        // region Stock Inputs
        {
          path: 'stockInputs',
          name: 'retailer.stockInputs.show',
          component: () => import('@/views/retailer/stockInputs/show.vue'),
          beforeEnter: deprecatedRoute,
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.stocks')
            }
          }
        },
        // endregion Stock Inputs

        // region Current Account
        {
          path: 'current-account/activities',
          name: 'retailer.current-account.activities.index',
          component: () => import('@/views/retailer/currentAccountActivities/index.vue'),
          props: true,
          meta: {
            breadcrumb: {
              name: t('router.transactions')
            },
            allowForAllRetailerTypes: true,
            allowWithoutSalesChannels: () => true
          }
        },
        // endregion

        // region Tarfin Pro

        {
          path: 'tarfin-pro',
          name: 'retailer.tarfin-pro',
          beforeEnter: deprecatedRoute,
          component: () => import('@/views/retailer/tarfinPro/index.vue'),
          meta: {
            breadcrumb: { name: t('router.tarfin_pro') }
          }
        },

        // endregion

        // region purchase requests
        {
          path: 'purchase-offers',
          name: 'retailer.purchase-offers.index',
          component: () => import('@/views/retailer/purchaseOffers/index.vue'),
          meta: {
            breadcrumb: { name: t('router.purchase_requests') }
          }
        },
        {
          path: 'purchase-offers/:id',
          name: 'retailer.purchase-offers.show',
          component: () => import('@/views/retailer/purchaseOffers/show.vue'),
          meta: {
            breadcrumb: {
              name: t('router.purchase_requests'),
              parent: 'retailer.purchase-offers.index'
            }
          }
        },
        // endregion

        // region Tarfin Pro Quotations - Requests
        {
          path: 'tarfin-pro-requests',
          name: 'retailer.tarfin-pro-requests.index',
          beforeEnter: deprecatedRoute,
          component: () => import('@/views/retailer/tarfinProRequests/index.vue'),
          meta: {
            breadcrumb: { name: t('router.tarfin_pro_requests') }
          }
        },
        // endregion

        // region Tarfin Pro Quotations - Create Request
        {
          path: 'tarfin-pro-requests/create',
          name: 'retailer.tarfin-pro-requests.create',
          beforeEnter: deprecatedRoute,
          component: () => import('@/views/retailer/tarfinProRequests/create.vue'),
          meta: {
            breadcrumb: { name: t('router.tarfin_pro_request_detail') }
          }
        },
        // endregion

        // region Tarfin Pro Quotations - Show Request
        {
          path: 'tarfin-pro-requests/:id',
          name: 'retailer.tarfin-pro-requests.show',
          beforeEnter: deprecatedRoute,
          component: () => import('@/views/retailer/tarfinProRequests/show.vue'),
          meta: {
            breadcrumb: { name: t('router.tarfin_pro_request_detail') }
          }
        },
        // endregion

        // region New Application V4
        {
          path: 'application/create',
          name: 'retailer.application.create',
          component: () => import('@/views/retailer/application/create/index.vue'),
          beforeEnter: [retailerForwardSaleBlocker],
          meta: {
            breadcrumb: { name: 'Yeni Satış' },
            scrollPosition: 0
          }
        },
        {
          path: 'application/:id/farmer',
          name: 'retailer.application.farmer.index',
          component: () => import('@/views/retailer/application/farmerInformation/index.vue'),
          meta: {
            breadcrumb: { name: 'Çiftçi Kayıt Formu' },
            scrollPosition: 0
          }
        },
        {
          path: 'application/:id/guarantor',
          name: 'retailer.application.guarantor.index',
          component: () => import('@/views/retailer/application/guarantorInformation/index.vue'),
          meta: {
            breadcrumb: { name: 'Satış yap' },
            scrollPosition: 0
          }
        },
        {
          path: 'application/:id/fv-document-waiting',
          name: 'retailer.application.fvDocumentWaiting',
          component: () => import('@/views/retailer/application/fvDocumentWaiting/index.vue'),
          meta: {
            breadcrumb: { name: 'Satış Yap' },
            scrollPosition: 0
          }
        },
        {
          path: 'application/:id/fv-retailer-approval',
          name: 'retailer.application.fvRetailerApproval',
          component: () => import('@/views/retailer/application/fvRetailerApproval.vue'),
          meta: {
            breadcrumb: { name: 'Satış yap' },
            scrollPosition: 0
          }
        },
        {
          path: 'application/:id/decision',
          name: 'retailer.application.decision',
          component: () => import('@/views/retailer/application/decision/index.vue'),
          meta: {
            breadcrumb: { name: 'Satış Yap' },
            scrollPosition: 0
          }
        },
        {
          path: 'application/:id/farmer-verification',
          name: 'retailer.application.fraud',
          component: () => import('@/views/retailer/application/fraudDarkDetected.vue'),
          meta: {
            breadcrumb: { name: 'Satış Yap' },
            scrollPosition: 0
          }
        },
        {
          path: 'application/:id/farmer-approval-waiting',
          name: 'retailer.application.farmer.waiting',
          component: () => import('@/views/retailer/application/farmerApprovalWaiting.vue'),
          meta: {
            breadcrumb: { name: 'Satış Yap' },
            scrollPosition: 0
          }
        },
        {
          path: 'application/:id/review',
          name: 'retailer.application.review',
          component: () => import('@/views/retailer/application/review/index.vue'),
          meta: {
            breadcrumb: { name: 'Satış Yap' },
            scrollPosition: 0
          }
        },
        {
          path: 'application/:id/payment-waiting',
          name: 'retailer.application.payment.waiting',
          component: () => import('@/views/retailer/application/paymentWaiting/index.vue'),
          meta: {
            breadcrumb: {
              name: 'Kredi Kartı ile Satış',
              parent: 'retailer.application.create'
            },
            scrollPosition: 0
          }
        },
        {
          path: 'application/:id/payment-success',
          name: 'retailer.application.payment.success',
          component: () => import('@/views/retailer/application/paymentSuccess.vue'),
          meta: {
            breadcrumb: {
              name: 'Kredi Kartı ile Satış',
              parent: 'retailer.application.create'
            },
            scrollPosition: 0
          }
        },
        {
          path: 'application/:id/documents',
          name: 'retailer.application.documents',
          component: () => import('@/views/retailer/application/documents/documents.vue'),
          props: true,
          meta: {
            breadcrumb: { name: 'Satış Yap' },
            scrollPosition: 0
          }
        },
        {
          path: 'findeks-requests/:findeks_request_id',
          name: 'retailers.findeks-requests.show',
          component: () => import('@/views/retailer/findeksRequests/show.vue'),
          props: true,
          meta: {
            breadcrumb: { name: 'Findeks Raporu' },
            scrollPosition: 0,
            allowWithoutSalesChannels: () => true,
            // The findeks report belongs to the retailer, not to a sales line, so every flex line is
            // listed here. Carrying only one of them shuts the other lines out of a page they own.
            allowForConsumerGoods: true,
            allowForCarSales: true,
            allowForIyiFinans: true,
            allowForTractorSales: true
          }
        },
        // endregion

        // region Consumer Goods Application
        {
          path: 'applications/consumer-goods/create',
          name: 'retailer.applications.consumer-goods.create',
          component: () => import('@/views/retailer/consumerGoodsApplication/create/index.vue'),
          // beforeEnter: [retailerForwardSaleBlocker],
          meta: {
            breadcrumb: { name: "Tarfin'le Sat" },
            scrollPosition: 0,
            allowForConsumerGoods: true,
            allowForAgriculture: false
          }
        },
        {
          path: 'applications/consumer-goods/:machineId',
          name: 'retailer.applications.consumer-goods.show',
          component: () => import('@/views/retailer/consumerGoodsApplication/create/index.vue'),
          props: true,
          meta: {
            machineOwnership: ConsumerGoodsCustomerInfoService,
            breadcrumb: { name: "Tarfin'le Sat" },
            scrollPosition: 0,
            allowForConsumerGoods: true,
            allowForAgriculture: false
          }
        },

        // region Car Sales Application
        {
          path: 'applications/car-sales/create',
          name: 'retailer.applications.car-sales.create',
          component: () => import('@/views/retailer/carSalesApplication/create/index.vue'),
          meta: {
            breadcrumb: { name: 'Araç Satış Başvurusu' },
            scrollPosition: 0,
            allowForCarSales: true,
            allowForAgriculture: false,
            middleware: [retailerCarSalesBlocker]
          }
        },
        {
          path: 'applications/iyi-finans-car-sales/create',
          name: 'retailer.applications.iyi-finans-car-sales.create',
          component: () => import('@/views/retailer/iyiFinansCarSalesApplication/create/index.vue'),
          meta: {
            breadcrumb: { name: 'Araç Satış Başvurusu' },
            scrollPosition: 0,
            allowForIyiFinans: true,
            allowForAgriculture: false,
            middleware: [retailerIyiFinansBlocker]
          }
        },
        {
          path: 'applications/tractor-sales/create',
          name: 'retailer.applications.tractor-sales.create',
          component: () => import('@/views/retailer/tractorSalesApplication/create/index.vue'),
          meta: {
            breadcrumb: { name: 'Traktör Satış Başvurusu' },
            scrollPosition: 0,
            allowForTractorSales: true,
            allowForAgriculture: false
          }
        },
        {
          path: 'applications/car-sales/:machineId',
          name: 'retailer.applications.car-sales.show',
          component: () => import('@/views/retailer/carSalesApplication/show.vue'),
          props: true,
          meta: {
            machineOwnership: RetailerCarSalesService,
            breadcrumb: { name: 'Araç Satış Başvurusu' },
            scrollPosition: 0,
            allowForCarSales: true,
            allowForAgriculture: false,
            middleware: [retailerCarSalesBlocker]
          }
        },
        {
          path: 'applications/iyi-finans-car-sales/:machineId',
          name: 'retailer.applications.iyi-finans-car-sales.show',
          component: () => import('@/views/retailer/iyiFinansCarSalesApplication/show.vue'),
          props: true,
          meta: {
            machineOwnership: RetailerIyiFinansCarSalesService,
            breadcrumb: { name: 'Araç Satış Başvurusu' },
            scrollPosition: 0,
            allowForIyiFinans: true,
            allowForAgriculture: false,
            middleware: [retailerIyiFinansBlocker]
          }
        },
        {
          path: 'applications/tractor-sales/:machineId',
          name: 'retailer.applications.tractor-sales.show',
          component: () => import('@/views/retailer/tractorSalesApplication/show.vue'),
          props: true,
          meta: {
            machineOwnership: RetailerTractorSalesService,
            breadcrumb: { name: 'Traktör Satış Başvurusu' },
            scrollPosition: 0,
            allowForTractorSales: true,
            allowForAgriculture: false
          }
        },
        {
          path: 'applications/tractor-sales/:machineId/counter-offer',
          name: 'retailer.applications.tractor-sales.counterOffer',
          component: () => import('@/views/retailer/tractorSalesApplication/counterOffer.vue'),
          props: true,
          meta: {
            machineOwnership: RetailerTractorSalesService,
            breadcrumb: { name: 'Karşı Teklif' },
            scrollPosition: 0,
            allowForTractorSales: true,
            allowForAgriculture: false
          }
        },
        {
          path: 'applications/tractor-sales/:machineId/guarantor',
          name: 'retailer.applications.tractor-sales.guarantor',
          component: () => import('@/views/retailer/tractorSalesApplication/guarantor/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: 'Traktör Satış Başvurusu' },
            scrollPosition: 0,
            allowForTractorSales: true,
            allowForAgriculture: false
          }
        },
        {
          path: 'applications/tractor-sales/:machineId/documents',
          name: 'retailer.applications.tractor-sales.documents',
          component: () => import('@/views/retailer/tractorSalesApplication/documents/documents.vue'),
          props: true,
          meta: {
            machineOwnership: RetailerTractorSalesService,
            breadcrumb: { name: 'Başvuru Belgeleri' },
            scrollPosition: 0,
            allowForTractorSales: true,
            allowForAgriculture: false
          }
        },
        {
          path: 'applications/tractor-sales/:machineId/guarantor-documents',
          name: 'retailer.applications.tractor-sales.guarantor-documents',
          component: () => import('@/views/retailer/tractorSalesApplication/documents/documents.vue'),
          props: route => ({ machineId: route.params.machineId, subject: 'guarantor' }),
          meta: {
            breadcrumb: { name: 'Kefil Belgeleri' },
            scrollPosition: 0,
            allowForTractorSales: true,
            allowForAgriculture: false
          }
        },
        {
          path: 'applications/car-sales/:machineId/counter-offer',
          name: 'retailer.applications.car-sales.counterOffer',
          component: () => import('@/views/retailer/carSalesApplication/counterOffer.vue'),
          props: true,
          meta: {
            machineOwnership: RetailerCarSalesService,
            breadcrumb: { name: 'Karşı Teklif' },
            scrollPosition: 0,
            allowForCarSales: true,
            allowForAgriculture: false,
            middleware: [retailerCarSalesBlocker]
          }
        },
        {
          path: 'applications/car-sales/:machineId/guarantor',
          name: 'retailer.applications.car-sales.guarantor',
          component: () => import('@/views/retailer/carSalesApplication/guarantor/index.vue'),
          props: true,
          meta: {
            breadcrumb: { name: 'Araç Satış Başvurusu' },
            scrollPosition: 0,
            allowForCarSales: true,
            allowForAgriculture: false,
            middleware: [retailerCarSalesBlocker]
          }
        },
        {
          path: 'applications/car-sales/:machineId/documents',
          name: 'retailer.applications.car-sales.documents',
          component: () => import('@/views/retailer/carSalesApplication/documents/documents.vue'),
          props: true,
          meta: {
            machineOwnership: RetailerCarSalesService,
            breadcrumb: { name: 'Başvuru Belgeleri' },
            scrollPosition: 0,
            allowForCarSales: true,
            allowForAgriculture: false,
            middleware: [retailerCarSalesBlocker]
          }
        },
        {
          path: 'applications/car-sales/:machineId/guarantor-documents',
          name: 'retailer.applications.car-sales.guarantor-documents',
          component: () => import('@/views/retailer/carSalesApplication/documents/documents.vue'),
          props: route => ({ machineId: route.params.machineId, subject: 'guarantor' }),
          meta: {
            breadcrumb: { name: 'Kefil Belgeleri' },
            scrollPosition: 0,
            allowForCarSales: true,
            allowForAgriculture: false,
            middleware: [retailerCarSalesBlocker]
          }
        },
        // endregion
        {
          path: 'applications/consumer-goods/:id/decision',
          name: 'retailer.applications.consumer-goods.decision',
          component: () => import('@/views/retailer/consumerGoodsApplication/decision.vue'),
          meta: {
            breadcrumb: { name: "Tarfin'le Sat" },
            scrollPosition: 0,
            allowForConsumerGoods: true,
            allowForAgriculture: false
          }
        },
        {
          path: 'applications/consumer-goods/:id/reject',
          name: 'retailer.applications.consumer-goods.reject',
          component: () => import('@/views/retailer/consumerGoodsApplication/reject.vue'),
          meta: {
            breadcrumb: { name: "Tarfin'le Sat" },
            scrollPosition: 0,
            allowForConsumerGoods: true,
            allowForAgriculture: false
          }
        },
        {
          path: 'applications/consumer-goods/:id/approve',
          name: 'retailer.applications.consumer-goods.approve',
          component: () => import('@/views/retailer/consumerGoodsApplication/approve.vue'),
          meta: {
            breadcrumb: { name: "Tarfin'le Sat" },
            scrollPosition: 0,
            allowForConsumerGoods: true,
            allowForAgriculture: false
          }
        },
        // endregion

        {
          path: 'dealership-contract',
          name: 'retailer.dealership-contract',
          component: () => import('@/views/retailer/documents/dealershipContract.vue'),
          props: true,
          meta: {
            breadcrumb: { name: 'Bayilik Sözleşmesi' }
          }
        }
      ]
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: to => ({ name: '404', params: { resource: 'page' } })
    }
  ]
}

export const createRouter = app => {
  const router = createVueRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: createRoutes(),

    scrollBehavior(to, from, savedPosition) {
      if (to.hash) {
        return {
          el: to.hash,
          behavior: 'smooth'
        }
      }

      if (to.meta?.scrollPosition !== undefined) {
        return { top: to.meta?.scrollPosition }
      }
    }
  })

  // const originalPush = router.push.prototype
  // router.push.prototype = function push(location) {
  //   return originalPush.call(this, location).catch(err => {})
  // }

  router.beforeEach((routeTo, routeFrom, next) => {
    const firstPath = window.location.pathname.split('/').filter(pathName => pathName)[0]
    const isRetailerPath = firstPath === 'retailer' || firstPath === '/'
    const authStore = isRetailerPath ? useAuthStore() : useAdminAuthStore()
    const pageLoadingStore = usePageLoadingStore()

    const pageViewEventValues = {
      page: {
        name: routeTo?.meta?.breadcrumb?.name,
        path: routeTo.path
      }
    }

    if (routeTo.meta?.suspense?.enabled === true) {
      Nprogress.start()
    }

    set({ button: null })
    authStore.setNextRoute(routeTo?.fullPath)

    if (authStore.getToken && routeTo.name !== 'maintenance') {
      // Sync cross-tab token before getUser so a fresh token avoids a 401-induced logout.
      authStore.syncFromStorage()

      // Show full-page overlay while getUser is in flight (only if it takes longer than start() delay to avoid flash on cached navigations).
      pageLoadingStore.start()

      authStore
        .getUser()
        .then(() => {
          const user = authStore.user

          if (!user) {
            const notification = {
              type: 'danger',

              message: t('router.access_denied')
            }

            const notificationStore = useNotificationStore()
            notificationStore.add(notification)
          } else {
            pageViewEventValues.user = {
              email: user.hash_email,
              signUpDate: moment(user.created_at).format('YYYY-MM-DD'),
              tid: user.id
            }
          }

          if (!isRetailerPath) {
            if (
              !routeTo.meta.permissions ||
              (routeTo.meta.permissions && app.config.globalProperties.$can(routeTo.meta.permissions))
            ) {
              Nprogress.start()

              next()

              return
            } else {
              let nextRoute = null

              if (country.is('ro')) {
                nextRoute = RO_ROLE_ROUTES.find(route => {
                  if (route.name === user.roles[0]?.name) {
                    return route
                  }
                  return undefined
                })
              } else {
                nextRoute = TR_ROLE_ROUTES.find(route => {
                  if (route.name === user.roles[0]?.name) {
                    return route
                  }
                  return undefined
                })
              }

              if (nextRoute) {
                next({
                  name: nextRoute.path
                })
              } else {
                const notification = {
                  type: 'danger',
                  message: t('router.access_denied')
                }

                const notificationStore = useNotificationStore()
                notificationStore.add(notification)
              }
            }

            return
          } else {
            Nprogress.start()

            // Block cross-tab navigation to routes the current retailer type cannot access.
            //
            // Mirrors retailerConsumerGoodsBlocker / retailerCarSalesBlocker / retailerIyiFinansBlocker,
            // which the parent route's beforeEnter chain does not re-run on child -> child navigation.
            if (isRetailerPath && !Retailer.canAccessRouteMeta(routeTo.meta)) {
              next({ name: 'retailer.dashboard' })
              return
            }

            const nextAfterOwnership = retailerMachineOwnership(routeTo, routeFrom, next)

            if (routeFrom.matched.length > 0 && routeTo.matched.length > 0 && routeTo.matched[0].beforeEnter) {
              const middlewares = routeTo.matched[0].beforeEnter
              const context = { routeTo, routeFrom, next: nextAfterOwnership }

              middlewares[0](routeTo, routeFrom, middlewarePipeline(context, middlewares, 1))
            } else {
              nextAfterOwnership()
            }
          }

          set(pageViewEventValues)
          event('page_view')
        })
        .catch(() => {
          // getUser failed (e.g., 401, network error)
          // For 401: the interceptor already triggers logout + reload
          // For other errors: redirect to login page
          Nprogress.start()
          if (isRetailerPath) {
            next({
              path: '/login',
              query: { redirect: routeTo.fullPath }
            })
          } else {
            next({
              path: '/admin/login',
              query: { redirect: routeTo.fullPath }
            })
          }
        })
        .finally(() => {
          pageLoadingStore.stop()
        })
    } else {
      Nprogress.start()

      set(pageViewEventValues)
      event('page_view')

      next()

      return
    }
  })

  router.afterEach(() => {
    Nprogress.done()
  })
  return router
}
